import { useEffect, useState, useCallback, useRef, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Clock, AlertTriangle, CheckCircle, RefreshCw,
  Edit2, X, Users, UserX, ThumbsUp, ThumbsDown, Hourglass,
  ChevronDown, ChevronRight, FileSpreadsheet, PlusCircle,
  Download, MessageSquare, Trash2, Scissors, RotateCcw, Umbrella, Copy,
} from "lucide-react";
import toast from "react-hot-toast";

// ── Types ────────────────────────────────────────────────────────────────────

interface ClockBreak {
  id: string;
  breakStartAt: string;
  breakEndAt: string | null;
}

interface ClockExclusion {
  id: string;
  startTime: string;
  endTime: string;
  excludedMinutes: number;
  reason: string;
  createdAt: string;
  restoredAt: string | null;
}

interface ClockEntry {
  id: string;
  teamMemberId: string;
  workDate: string;
  clockInAt: string | null;
  clockOutAt: string | null;
  adjustedClockInAt?: string | null;
  adjustedClockOutAt?: string | null;
  scheduledStart: string | null;
  scheduledEnd: string | null;
  timezone: string | null;
  flagStatus: string;
  clockInFlag: string | null;
  clockOutFlag: string | null;
  approvalStatus: string;
  overrideReason: string | null;
  overriddenBy: string | null;
  overriddenAt: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt?: string;
  breaks: ClockBreak[];
  totalBreakMinutes: number;
  totalWorkedMinutes: number | null;
  totalOvertimeMinutes: number | null;
  clockInDeviationMinutes: number | null;
  clockOutDeviationMinutes: number | null;
  memberName?: string | null;
  memberFirstName?: string | null;
  teamId?: string | null;
  role?: string | null;
  regionId?: string | null;
  pendingAdjustments?: number;
  exclusions?: ClockExclusion[];
  isScheduledOnly?: boolean;
}

interface PendingAdj {
  id: string;
  teamMemberId: string;
  memberName?: string | null;
  workDate: string;
  field: string;
  adjustmentType: string;
  requestedValue: string;
  originalValue?: string | null;
  requestedAt: string;
  resolvedAt?: string | null;
  reason: string;
  rejectionReason?: string | null;
  status: string;
  scheduledStart?: string | null;
  scheduledEnd?: string | null;
  clockInAt?: string | null;
  clockOutAt?: string | null;
  timezone?: string | null;
}

interface MissingMember {
  teamMemberId: string;
  name: string;
  teamId: string | null;
  scheduledStart: string;
  minutesLate: number;
}

interface Coverage {
  scheduled: number;
  clockedIn: number;
  gap: number;
}

interface ErrorEntry {
  id: string;
  teamMemberId: string;
  memberName: string;
  memberFirstName?: string | null;
  workDate: string;
  clockInAt: string;
  clockOutAt: string | null;
  durationLabel: string;
  durationMins: number;
  errors: string[];
  timezone: string | null;
  role?: string | null;
  regionId?: string | null;
}

interface TimeOffApprovalRow {
  id: string;
  teamMemberId: string;
  memberName: string;
  teamId?: string | null;
  regionId?: string | null;
  startDate: string;
  endDate: string;
  requestType: "time_off" | "sick" | "personal";
  reason?: string | null;
  status: "pending" | "approved" | "rejected";
  reviewNote?: string | null;
  createdAt: string;
  reviewedAt?: string | null;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const FLAG_CONFIG: Record<string, { label: string; bg: string; text: string }> = {
  pending:         { label: "In Progress", bg: "bg-blue-50",   text: "text-blue-700" },
  ok:              { label: "On Time",     bg: "bg-green-50",  text: "text-green-700" },
  late:            { label: "Late",        bg: "bg-yellow-50", text: "text-yellow-700" },
  early_departure: { label: "Early Out",   bg: "bg-orange-50", text: "text-orange-700" },
  overtime:        { label: "Overtime",    bg: "bg-red-50",    text: "text-red-700" },
  unscheduled:     { label: "Unscheduled", bg: "bg-red-50",    text: "text-red-700" },
};

function toDatetimeLocalInTz(iso: string | null | undefined, tz: string | null | undefined): string {
  if (!iso) return "";
  const date = new Date(iso);
  const useTz = tz || DISPLAY_TZ;
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: useTz,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit",
    hour12: false,
  }).formatToParts(date);
  const get = (t: string) => parts.find(p => p.type === t)!.value;
  const h = get("hour");
  return `${get("year")}-${get("month")}-${get("day")}T${h === "24" ? "00" : h}:${get("minute")}`;
}

function parseDatetimeLocalInTz(value: string, tz: string | null | undefined): string {
  const useTz = tz || DISPLAY_TZ;
  const refDate = new Date(value + "Z");
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: useTz,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
    hour12: false,
  }).formatToParts(refDate);
  const get = (t: string) => parseInt(parts.find(p => p.type === t)!.value);
  const tzMs = Date.UTC(get("year"), get("month") - 1, get("day"), get("hour") % 24, get("minute"), get("second"));
  return new Date(refDate.getTime() + (refDate.getTime() - tzMs)).toISOString();
}

function tzLabel(tz: string | null | undefined): string {
  const useTz = tz || DISPLAY_TZ;
  return new Intl.DateTimeFormat("en-US", { timeZone: useTz, timeZoneName: "short" })
    .formatToParts(new Date())
    .find(p => p.type === "timeZoneName")?.value ?? useTz;
}

const DISPLAY_TZ = "America/Los_Angeles";

function fmt(iso: string | null | undefined, _tz?: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", timeZone: DISPLAY_TZ });
}

// Full date + time — unlike fmt() above, this doesn't hide the date. Used for adjustment
// request values, where a manager needs to see the actual submitted date to catch a
// wrong-date submission (e.g. requestedValue landing on a different day than workDate).
function fmtFull(iso: string | null | undefined, tz?: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", {
    month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", timeZone: tz || DISPLAY_TZ,
  });
}

// Local (timezone-aware) YYYY-MM-DD for a timestamp — used to compare against workDate.
function localDateStr(iso: string, tz?: string | null): string {
  return new Date(iso).toLocaleDateString("en-CA", { timeZone: tz || DISPLAY_TZ });
}

// True when a submitted timestamp's own calendar date doesn't match the workDate the
// request is filed under — the exact pattern that produces "shift duration over 24h" or
// "clock-out before clock-in" errors that otherwise look inexplicable in the UI.
function dateMismatch(iso: string | null | undefined, workDate: string, tz?: string | null): boolean {
  if (!iso) return false;
  return localDateStr(iso, tz) !== workDate;
}

// Explains a date-mismatched request in terms of the actual gap from clock-in, so a manager
// can tell "impossible — wrong date" (clock-out before clock-in, or absurdly long) apart from
// "plausible — forgot to clock out overnight" (a day later but a reasonable shift length).
function explainDateMismatch(
  requestedIso: string,
  clockInIso: string | null | undefined,
  workDate: string
): { text: string; severity: "impossible" | "long" | "overnight" } {
  if (!clockInIso) {
    return { text: `Requested date doesn't match work date (${workDate}) — verify before approving.`, severity: "long" };
  }
  const hours = (new Date(requestedIso).getTime() - new Date(clockInIso).getTime()) / 3_600_000;
  const fmtHrs = (h: number) => `${Math.floor(Math.abs(h))}h ${Math.round((Math.abs(h) % 1) * 60)}m`;
  if (hours < 0) {
    return { text: `Requested time is BEFORE the clock-in (by ${fmtHrs(hours)}) — likely the wrong date was entered.`, severity: "impossible" };
  }
  if (hours > 16) {
    return { text: `${fmtHrs(hours)} after clock-in — exceeds a normal shift length, likely the wrong date was entered.`, severity: "long" };
  }
  return { text: `Crosses into the next day — ${fmtHrs(hours)} after clock-in. Verify this was an overnight shift, not a wrong date.`, severity: "overnight" };
}

function fmtMins(mins: number | null): string {
  if (mins === null) return "—";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

async function apiRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, { headers: { "Content-Type": "application/json" }, credentials: "include", ...init });
  if (res.status === 204) return {} as T;
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(data?.message || "Request failed"), data);
  return data as T;
}

function adjTypeLabel(type: string, field: string) {
  if (type === "approval_clock_in")  return "Early Clock-In";
  if (type === "approval_clock_out") return "Overtime Clock-Out";
  if (type === "incorrect_time")     return field === "clock_in" ? "Incorrect Clock-In" : "Incorrect Clock-Out";
  if (type === "missed_clock_in")    return "Missed Clock-In";
  if (type === "missed_clock_out")   return "Missed Clock-Out";
  if (type === "missed_break")       return "Missed Break";
  if (type === "incorrect_break")    return "Incorrect Break";
  return "Adjustment";
}

function adjBadgeColor(type: string) {
  if (type === "approval_clock_in")  return "bg-blue-100 text-blue-700";
  if (type === "approval_clock_out") return "bg-orange-100 text-orange-700";
  return "bg-purple-100 text-purple-700";
}

type AdjGroup =
  | { kind: "single";     adj:   PendingAdj;                     key: string }
  | { kind: "missed_day"; adjIn: PendingAdj; adjOut: PendingAdj; key: string };

function groupAdjustments(adjs: PendingAdj[]): AdjGroup[] {
  const missedIns  = adjs.filter((a) => a.adjustmentType === "missed_clock_in");
  const missedOuts = adjs.filter((a) => a.adjustmentType === "missed_clock_out");
  const others     = adjs.filter((a) => a.adjustmentType !== "missed_clock_in" && a.adjustmentType !== "missed_clock_out");
  const usedOutIds = new Set<string>();

  const groups: AdjGroup[] = missedIns.map((adjIn) => {
    const adjOut = missedOuts.find(
      (a) => a.teamMemberId === adjIn.teamMemberId && a.workDate === adjIn.workDate
    );
    if (adjOut) {
      usedOutIds.add(adjOut.id);
      return { kind: "missed_day" as const, adjIn, adjOut, key: adjIn.id };
    }
    return { kind: "single" as const, adj: adjIn, key: adjIn.id };
  });

  missedOuts
    .filter((a) => !usedOutIds.has(a.id))
    .forEach((adj) => groups.push({ kind: "single", adj, key: adj.id }));

  others.forEach((adj) => groups.push({ kind: "single", adj, key: adj.id }));

  return groups;
}

function adjBreakMins(adjOut: PendingAdj): number {
  if (!adjOut.originalValue) return 0;
  const mins = Math.round(new Date(adjOut.originalValue).getTime() / 60_000);
  return mins > 0 && mins <= 720 ? mins : 0;
}

// ── Component ────────────────────────────────────────────────────────────────

type Tab = "attendance" | "approvals" | "time-off-approvals" | "missing" | "timesheets" | "errors";

function RejectReasonInput({ onConfirm, onCancel }: { onConfirm: (reason: string) => void; onCancel: () => void }) {
  const [reason, setReason] = useState("");
  return (
    <div className="flex items-center gap-2">
      <input
        type="text"
        autoFocus
        placeholder="Reason for rejection..."
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        className="flex-1 rounded-md border border-gray-300 text-xs px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-red-400"
      />
      <button
        onClick={() => onConfirm(reason)}
        className="px-3 py-1.5 rounded-md bg-red-600 text-white text-xs font-semibold hover:bg-red-700"
      >Confirm</button>
      <button onClick={onCancel} className="px-3 py-1.5 rounded-md border border-gray-300 text-xs text-gray-600 hover:bg-gray-50">Cancel</button>
    </div>
  );
}

export function ClockManager() {
  const today = new Date().toISOString().slice(0, 10);

  const [searchParams, setSearchParams] = useSearchParams();
  const tab = (searchParams.get("tab") as Tab | null) ?? "attendance";
  const setTab = (t: Tab) => setSearchParams((p) => { const n = new URLSearchParams(p); n.set("tab", t); return n; }, { replace: true });

  // Attendance tab
  const [entries, setEntries]       = useState<ClockEntry[]>([]);
  const [loading, setLoading]       = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [missing, setMissing]   = useState<MissingMember[]>([]);
  const [coverage, setCoverage] = useState<Coverage | null>(null);

  // Filters
  const [startDate, setStartDate]   = useState(today);
  const [endDate, setEndDate]       = useState(today);
  const [filterFlag, setFilterFlag] = useState("");
  const [attSearch, setAttSearch]   = useState("");
  const [attRegion, setAttRegion]   = useState("");
  const [attRole, setAttRole]       = useState("");

  // Approvals tab
  const [pendingAdjs, setPendingAdjs]           = useState<PendingAdj[]>([]);
  const [resolvedAdjs, setResolvedAdjs]         = useState<PendingAdj[]>([]);
  const [rejectingId, setRejectingId]           = useState<string | null>(null);
  const [pendingCount, setPendingCount]         = useState(0);
  const [retractingId, setRetractingId]         = useState<string | null>(null);

  // Time-off approvals tab
  const [toRequests, setToRequests]           = useState<TimeOffApprovalRow[]>([]);
  const [toHistory, setToHistory]             = useState<TimeOffApprovalRow[]>([]);
  const [toPendingCount, setToPendingCount]   = useState(0);
  const [toRejectingId, setToRejectingId]     = useState<string | null>(null);
  const [toRejectNote, setToRejectNote]       = useState<Record<string, string>>({});
  const [toApprovingId, setToApprovingId]     = useState<string | null>(null);
  const [toRevertingId, setToRevertingId]     = useState<string | null>(null);
  const [toApproveNoteMap, setToApproveNoteMap] = useState<Record<string, string>>({});

  // Errors tab
  const [errorEntries, setErrorEntries]   = useState<ErrorEntry[]>([]);
  const [errorsLoading, setErrorsLoading] = useState(false);
  const [errorSearch, setErrorSearch]     = useState("");
  const [adjSearch, setAdjSearch]         = useState("");
  const [adjSortCol, setAdjSortCol]       = useState<"employee" | "date">("date");
  const [adjSortDir, setAdjSortDir]       = useState<"asc" | "desc">("desc");
  const [errorRegion, setErrorRegion]     = useState("");
  const [errorRole, setErrorRole]         = useState("");
  const [errorCount, setErrorCount]       = useState(0);
  const [selectedErrors, setSelectedErrors] = useState<Set<string>>(new Set());
  const [deletingId, setDeletingId]       = useState<string | null>(null);
  const [ignoringId, setIgnoringId]       = useState<string | null>(null);

  // Add Note modal
  const [activeEntry, setActiveEntry] = useState<ClockEntry | null>(null);
  const [noteText, setNoteText]       = useState("");
  const [saving, setSaving]           = useState(false);

  // Edit times modal
  const [editEntry, setEditEntry]         = useState<ClockEntry | null>(null);
  const [editClockIn, setEditClockIn]     = useState("");
  const [editClockOut, setEditClockOut]   = useState("");
  const [editBreakMins, setEditBreakMins] = useState<number | "">("");
  const [editReason, setEditReason]       = useState("");
  const [savingEdit, setSavingEdit]       = useState(false);

  // Add Time Record modal (manager creates entry directly)
  const [showAddRecord, setShowAddRecord]     = useState(false);
  const [arMembers, setArMembers]             = useState<{ id: string; name: string; timezone: string | null }[]>([]);
  const [arMemberId, setArMemberId]           = useState("");
  const [arDate, setArDate]                   = useState(today);
  const [arClockIn, setArClockIn]             = useState("");
  const [arClockOut, setArClockOut]           = useState("");
  const [arBreakMins, setArBreakMins]         = useState<number | "">(30);
  const [arNotes, setArNotes]                 = useState("");
  const [savingAr, setSavingAr]               = useState(false);
  const [arSearch, setArSearch]               = useState("");
  const [arDropdownOpen, setArDropdownOpen]   = useState(false);

  // Exclusion modal
  const [exclEntry, setExclEntry]         = useState<ClockEntry | null>(null);
  const [exclStart, setExclStart]         = useState("");
  const [exclEnd, setExclEnd]             = useState("");
  const [exclReason, setExclReason]       = useState("");
  const [savingExcl, setSavingExcl]       = useState(false);
  const [restoringExclId, setRestoringExclId] = useState<string | null>(null);

  const pollRef          = useRef<ReturnType<typeof setInterval> | null>(null);
  const prevPendingCount = useRef(0);
  const tsReqId          = useRef(0);

  // ── Data loaders ────────────────────────────────────────────────────────

  const loadEntries = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      const params = new URLSearchParams();
      if (startDate)  params.set("startDate", startDate);
      if (endDate)    params.set("endDate", endDate);
      if (filterFlag) params.set("flagStatus", filterFlag);
      const res = await apiRequest<{ entries: ClockEntry[] }>(`/api/v1/clock/history?${params}`);
      setEntries(res.entries ?? []);
    } catch {
      toast.error("Failed to load attendance");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [startDate, endDate, filterFlag]);

  const loadPendingApprovals = useCallback(async () => {
    try {
      const [pendingRes, resolvedRes] = await Promise.all([
        apiRequest<{ adjustments: PendingAdj[] }>("/api/v1/clock/adjustments?status=pending"),
        apiRequest<{ adjustments: PendingAdj[] }>("/api/v1/clock/adjustments?startDate=" + new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)),
      ]);
      const pending = pendingRes.adjustments ?? [];
      setPendingAdjs(pending);
      setPendingCount(pending.length);
      setResolvedAdjs((resolvedRes.adjustments ?? []).filter((a) => a.status !== "pending"));
    } catch { /* silent */ }
  }, []);

  const loadMissingToday = useCallback(async () => {
    try {
      const res = await apiRequest<{ missing: MissingMember[]; coverage: Coverage }>("/api/v1/clock/missing-today");
      setMissing(res.missing ?? []);
      setCoverage(res.coverage ?? null);
    } catch { /* silent */ }
  }, []);

  const loadTimeOffApprovals = useCallback(async () => {
    try {
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
      const [pendingRes, historyRes] = await Promise.all([
        apiRequest<{ requests: TimeOffApprovalRow[] }>("/api/v1/time-off/pending"),
        apiRequest<{ requests: TimeOffApprovalRow[] }>(`/api/v1/time-off?status=approved&startDate=${thirtyDaysAgo}`),
      ]);
      const pending = pendingRes.requests ?? [];
      setToRequests(pending);
      setToPendingCount(pending.length);
      setToHistory(historyRes.requests ?? []);
    } catch { /* silent */ }
  }, []);

  // Timesheets tab
  const getWeekStart = () => {
    const d = new Date();
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff)).toISOString().split("T")[0];
  };
  const [tsEntries, setTsEntries]     = useState<ClockEntry[]>([]);
  const [tsLoading, setTsLoading]     = useState(false);
  const [tsStart, setTsStart]         = useState(getWeekStart);
  const [tsEnd, setTsEnd]             = useState(today);
  const [tsSearch, setTsSearch]       = useState("");
  const [tsRegion, setTsRegion]       = useState("");
  const [tsRole, setTsRole]           = useState("");
  const [tsRegions, setTsRegions]     = useState<{ id: string; name: string }[]>([]);
  const [expanded, setExpanded]       = useState<Set<string>>(new Set());
  const [tsNotesExpanded, setTsNotesExpanded] = useState<Set<string>>(new Set());
  const [deletingTsId, setDeletingTsId]       = useState<string | null>(null);

  const loadTimesheets = useCallback(async () => {
    const reqId = ++tsReqId.current;
    setTsLoading(true);
    try {
      const PAGE_SIZE = 1000;
      let page = 1;
      let allEntries: ClockEntry[] = [];
      while (true) {
        const params = new URLSearchParams({ startDate: tsStart, endDate: tsEnd, limit: String(PAGE_SIZE), page: String(page) });
        const res = await apiRequest<{ entries: ClockEntry[]; pagination: { total: number } }>(`/api/v1/clock/history?${params}`);
        if (reqId !== tsReqId.current) return; // stale — a newer request is in flight
        allEntries = allEntries.concat(res.entries ?? []);
        if (allEntries.length >= res.pagination.total) break;
        page++;
      }
      // Every fetched entry is shown — including shifts still in progress (no clock-out
      // yet at all), which render with a "Working" indicator instead of being hidden.
      const completed = allEntries;

      let gaps: ClockEntry[] = [];
      try {
        const gapsParams = new URLSearchParams({ startDate: tsStart, endDate: tsEnd });
        const gapsRes = await apiRequest<{ entries: ClockEntry[] }>(`/api/v1/clock/schedule-gaps?${gapsParams}`);
        if (reqId !== tsReqId.current) return;
        gaps = gapsRes.entries ?? [];
      } catch { /* schedule gaps are supplementary — don't fail the whole tab if this errors */ }

      setTsEntries([...completed, ...gaps]);
    } catch {
      if (reqId === tsReqId.current) toast.error("Failed to load timesheets");
    } finally {
      if (reqId === tsReqId.current) setTsLoading(false);
    }
  }, [tsStart, tsEnd]);

  useEffect(() => {
    if (tab === "timesheets") void loadTimesheets();
  }, [tab, loadTimesheets]);

  useEffect(() => {
    apiRequest<{ data: { id: string; name: string }[] }>("/api/v1/regions")
      .then((r) => setTsRegions(r.data ?? []))
      .catch(() => setTsRegions([]));
  }, []);

  const exportTimesheets = useCallback(() => {
    const filtered = tsEntries.filter((e) =>
      !tsSearch || (e.memberFirstName ?? e.memberName?.split(" ")[0] ?? "").toLowerCase().startsWith(tsSearch.toLowerCase())
    );
    if (filtered.length === 0) { toast.error("No data to export"); return; }

    const headers = ["Employee", "Date", "Clock In", "Clock Out", "Scheduled", "Break (mins)", "Worked (mins)", "Exclusions (mins)", "Overtime (mins)", "Exceptions", "Notes"];
    const rows = filtered
      .slice()
      .sort((a, b) => (a.memberName ?? "").localeCompare(b.memberName ?? "") || (a.workDate > b.workDate ? -1 : 1))
      .map((e) => {
        const effectiveIn  = e.adjustedClockInAt  ?? e.clockInAt;
        const effectiveOut = e.adjustedClockOutAt ?? e.clockOutAt;
        const inDev  = e.clockInDeviationMinutes;
        const outDev = e.clockOutDeviationMinutes;
        const exceptions: string[] = [];
        if (inDev !== null && inDev > 0)  exceptions.push(`Late +${inDev}m`);
        if (outDev !== null && outDev < 0) exceptions.push(`Early Out ${outDev}m`);
        if (outDev !== null && outDev > 0) exceptions.push(`OT +${outDev}m`);
        return [
          e.memberName ?? "",
          e.workDate,
          fmt(effectiveIn, e.timezone),
          fmt(effectiveOut, e.timezone),
          e.scheduledStart && e.scheduledEnd ? `${e.scheduledStart} – ${e.scheduledEnd}` : "",
          String(e.totalBreakMinutes ?? 0),
          String(e.totalWorkedMinutes ?? 0),
          String((e.exclusions ?? []).filter((x) => !x.restoredAt).reduce((s, x) => s + x.excludedMinutes, 0)),
          String(e.totalOvertimeMinutes ?? 0),
          exceptions.join("; "),
          e.notes ?? "",
        ];
      });

    const csv = [headers, ...rows]
      .map((r) => r.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href     = url;
    a.download = `timesheets_${tsStart}_to_${tsEnd}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} shifts`);
  }, [tsEntries, tsSearch, tsStart, tsEnd]);

  const [generatingDuplicates, setGeneratingDuplicates] = useState(false);

  const generateDuplicates = useCallback(async () => {
    if (!tsStart || !tsEnd) { toast.error("Select a From and To date first"); return; }
    setGeneratingDuplicates(true);
    try {
      const params = new URLSearchParams({ startDate: tsStart, endDate: tsEnd });
      const res = await fetch(`/api/v1/clock/entries/duplicates/export?${params}`, { credentials: "include" });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.message || "Failed to generate duplicates report");
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `clock-duplicates-${tsStart}-to-${tsEnd}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Duplicates report downloaded");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to generate duplicates report");
    } finally {
      setGeneratingDuplicates(false);
    }
  }, [tsStart, tsEnd]);

  const downloadCsv = (headers: string[], rows: string[][], filename: string) => {
    if (rows.length === 0) { toast.error("No data to export"); return; }
    const csv = [headers, ...rows]
      .map((r) => r.map((c) => `"${c.replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} row${rows.length !== 1 ? "s" : ""}`);
  };

  const exportAttendance = useCallback(() => {
    const rows = entries
      .filter((e) => {
        if (attSearch && !(e.memberFirstName ?? e.memberName?.split(" ")[0] ?? "").toLowerCase().startsWith(attSearch.toLowerCase())) return false;
        if (attRegion && e.regionId !== attRegion) return false;
        if (attRole && e.role !== attRole) return false;
        return true;
      })
      .map((e) => {
        const effectiveIn  = e.adjustedClockInAt  ?? e.clockInAt;
        const effectiveOut = e.adjustedClockOutAt ?? e.clockOutAt;
        const inFlag  = e.clockInFlag  ?? e.flagStatus;
        const outFlag = e.clockOutFlag ?? null;
        const exceptions = [inFlag, outFlag].filter(Boolean).filter((f) => f !== "ok" && f !== "pending") as string[];
        return [
          e.memberName ?? "",
          e.workDate,
          effectiveIn  ? new Date(effectiveIn).toLocaleString("en-US",  { timeZone: DISPLAY_TZ }) : "",
          effectiveOut ? new Date(effectiveOut).toLocaleString("en-US", { timeZone: DISPLAY_TZ }) : "Still In",
          e.scheduledStart && e.scheduledEnd ? `${e.scheduledStart} – ${e.scheduledEnd}` : "",
          String(e.totalWorkedMinutes ?? 0),
          e.approvalStatus,
          exceptions.join("; "),
          e.notes ?? "",
        ];
      });
    downloadCsv(
      ["Employee", "Date", "Clock In", "Clock Out", "Scheduled", "Worked (mins)", "Approval Status", "Exceptions", "Notes"],
      rows,
      `attendance_${startDate}_to_${endDate}.csv`,
    );
  }, [entries, attSearch, attRegion, attRole, startDate, endDate]);

  const exportApprovals = useCallback(() => {
    const all = [...pendingAdjs, ...resolvedAdjs];
    const rows = all.map((a) => [
      a.memberName ?? "",
      a.workDate,
      a.adjustmentType.replace(/_/g, " "),
      a.field.replace(/_/g, " "),
      a.requestedValue ? new Date(a.requestedValue).toLocaleString("en-US", { timeZone: DISPLAY_TZ }) : "",
      a.originalValue  ? new Date(a.originalValue).toLocaleString("en-US",  { timeZone: DISPLAY_TZ }) : "",
      a.reason ?? "",
      a.status,
      a.resolvedAt ? new Date(a.resolvedAt).toLocaleDateString("en-US") : "",
      a.rejectionReason ?? "",
    ]);
    const today = new Date().toISOString().slice(0, 10);
    downloadCsv(
      ["Employee", "Date", "Type", "Field", "Requested Value", "Original Value", "Reason", "Status", "Resolved At", "Rejection Reason"],
      rows,
      `approvals_${today}.csv`,
    );
  }, [pendingAdjs, resolvedAdjs]);

  const exportMissing = useCallback(() => {
    const rows = missing.map((m) => [
      m.name,
      m.scheduledStart,
      `+${m.minutesLate}m`,
    ]);
    const today = new Date().toISOString().slice(0, 10);
    downloadCsv(["Employee", "Scheduled Start", "Minutes Late"], rows, `not_clocked_in_${today}.csv`);
  }, [missing]);

  const exportErrors = useCallback(() => {
    const rows = errorEntries
      .filter((e) => {
        if (errorSearch && !(e.memberFirstName ?? e.memberName.split(" ")[0]).toLowerCase().startsWith(errorSearch.toLowerCase())) return false;
        if (errorRegion && e.regionId !== errorRegion) return false;
        if (errorRole && e.role !== errorRole) return false;
        return true;
      })
      .map((e) => [
        e.memberName,
        e.workDate,
        new Date(e.clockInAt).toLocaleString("en-US", { timeZone: DISPLAY_TZ }),
        e.clockOutAt ? new Date(e.clockOutAt).toLocaleString("en-US", { timeZone: DISPLAY_TZ }) : "Still In",
        e.durationLabel,
        e.errors.join("; "),
      ]);
    const today = new Date().toISOString().slice(0, 10);
    downloadCsv(["Employee", "Date", "Clock In", "Clock Out", "Duration", "Issues"], rows, `clock_errors_${today}.csv`);
  }, [errorEntries, errorSearch, errorRegion, errorRole]);

  const loadErrors = useCallback(async () => {
    setErrorsLoading(true);
    try {
      const res = await apiRequest<{ errors: ErrorEntry[] }>("/api/v1/clock/errors");
      const list = res.errors ?? [];
      setErrorEntries(list);
      setErrorCount(list.length);
      setSelectedErrors(new Set());
    } catch {
      toast.error("Failed to load clock-in errors");
    } finally { setErrorsLoading(false); }
  }, []);

  useEffect(() => {
    if (tab === "errors") void loadErrors();
  }, [tab, loadErrors]);

  const deleteEntry = async (id: string, source: "errors" | "timesheets" = "errors") => {
    if (source === "timesheets") setDeletingTsId(id);
    else setDeletingId(id);
    try {
      await apiRequest(`/api/v1/clock/entries/${id}`, { method: "DELETE" });
      setErrorEntries((prev) => prev.filter((e) => e.id !== id));
      setErrorCount((c) => Math.max(0, c - 1));
      setSelectedErrors((prev) => { const n = new Set(prev); n.delete(id); return n; });
      setTsEntries((prev) => prev.filter((e) => e.id !== id));
      toast.success("Entry deleted");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to delete");
    } finally {
      setDeletingId(null);
      setDeletingTsId(null);
    }
  };

  const deleteSelected = async () => {
    if (selectedErrors.size === 0) return;
    const ids = [...selectedErrors];
    for (const id of ids) await deleteEntry(id);
  };

  const ignoreError = async (id: string) => {
    setIgnoringId(id);
    try {
      await apiRequest(`/api/v1/clock/entries/${id}/ignore-error`, { method: "PATCH" });
      setErrorEntries((prev) => prev.filter((e) => e.id !== id));
      setErrorCount((c) => Math.max(0, c - 1));
      setSelectedErrors((prev) => { const n = new Set(prev); n.delete(id); return n; });
      toast.success("Error ignored — entry kept in timesheets");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to ignore");
    } finally {
      setIgnoringId(null);
    }
  };

  const addExclusion = async () => {
    if (!exclEntry || !exclStart || !exclEnd || !exclReason.trim()) {
      toast.error("Fill in all exclusion fields"); return;
    }
    setSavingExcl(true);
    try {
      const res = await apiRequest<{ exclusion: ClockExclusion }>(
        `/api/v1/clock/entries/${exclEntry.id}/exclusions`,
        { method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ startTime: exclStart, endTime: exclEnd, reason: exclReason.trim() }) }
      );
      // Update modal state so new exclusion shows immediately
      setExclEntry((prev) => prev ? {
        ...prev,
        exclusions: [...(prev.exclusions ?? []), res.exclusion],
      } : prev);
      setExclStart(""); setExclEnd(""); setExclReason("");
      // Reload to get accurate server-computed worked total
      void loadTimesheets();
      toast.success("Exclusion added");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to add exclusion");
    } finally {
      setSavingExcl(false);
    }
  };

  const restoreExclusion = async (exclId: string) => {
    setRestoringExclId(exclId);
    try {
      const res = await apiRequest<{ exclusion: ClockExclusion }>(
        `/api/v1/clock/exclusions/${exclId}/restore`, { method: "PATCH" }
      );
      // Update modal state optimistically so restore shows immediately
      setExclEntry((prev) => prev ? {
        ...prev,
        exclusions: (prev.exclusions ?? []).map((x) => x.id === exclId ? res.exclusion : x),
      } : prev);
      void loadTimesheets();
      toast.success("Exclusion restored — hours added back");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to restore");
    } finally {
      setRestoringExclId(null);
    }
  };

  // Always-current refs so the poll interval never captures a stale closure
  const loadEntriesRef   = useRef(loadEntries);
  const loadPendingRef   = useRef(loadPendingApprovals);
  const loadMissingRef   = useRef(loadMissingToday);
  const loadTimeOffRef   = useRef(loadTimeOffApprovals);
  useEffect(() => { loadEntriesRef.current = loadEntries; },           [loadEntries]);
  useEffect(() => { loadPendingRef.current = loadPendingApprovals; },  [loadPendingApprovals]);
  useEffect(() => { loadMissingRef.current = loadMissingToday; },      [loadMissingToday]);
  useEffect(() => { loadTimeOffRef.current = loadTimeOffApprovals; },  [loadTimeOffApprovals]);

  useEffect(() => {
    void Promise.all([loadPendingApprovals(), loadMissingToday(), loadTimeOffApprovals()]);
    pollRef.current = setInterval(() => {
      void loadEntriesRef.current();
      void loadPendingRef.current();
      void loadMissingRef.current();
      void loadTimeOffRef.current();
    }, 30_000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  // Auto-reload attendance whenever the date range or flag filter changes
  useEffect(() => {
    void loadEntries();
  }, [loadEntries]);

  // Auto-switch to Approvals tab when count goes up
  useEffect(() => {
    if (pendingCount > prevPendingCount.current && prevPendingCount.current > 0) {
      toast("New approval request received", { icon: "🔔" });
    }
    prevPendingCount.current = pendingCount;
  }, [pendingCount]);

  // ── Approval actions ────────────────────────────────────────────────────

  const approvePendingAdj = async (adj: PendingAdj) => {
    try {
      await apiRequest(`/api/v1/clock/adjustments/${adj.id}/approve`, { method: "PATCH", body: JSON.stringify({}) });
      setPendingAdjs((prev) => prev.filter((a) => a.id !== adj.id));
      setPendingCount((c) => Math.max(0, c - 1));
      if (adj.adjustmentType === "approval_clock_out") void loadEntries();
      toast.success("Approved");
      void loadPendingApprovals();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed");
    }
  };

  const rejectPendingAdj = async (adj: PendingAdj, reason: string) => {
    if (!reason.trim()) { toast.error("Rejection reason is required"); return; }
    try {
      await apiRequest(`/api/v1/clock/adjustments/${adj.id}/reject`, { method: "PATCH", body: JSON.stringify({ reason }) });
      setPendingAdjs((prev) => prev.filter((a) => a.id !== adj.id));
      setPendingCount((c) => Math.max(0, c - 1));
      setRejectingId(null);
      toast.success("Rejected");
      void loadPendingApprovals();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed");
    }
  };

  const retractAdj = async (adj: PendingAdj) => {
    setRetractingId(adj.id);
    try {
      await apiRequest(`/api/v1/clock/adjustments/${adj.id}/retract`, { method: "PATCH", body: JSON.stringify({}) });
      toast.success("Retracted — request moved back to pending");
      void loadPendingApprovals();
      void loadEntries();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to retract");
    } finally {
      setRetractingId(null);
    }
  };

  const approvePair = async (adjIn: PendingAdj, adjOut: PendingAdj) => {
    try {
      // Approve clock-in first (creates the entry), then clock-out (applies it + break)
      await apiRequest(`/api/v1/clock/adjustments/${adjIn.id}/approve`, { method: "PATCH", body: JSON.stringify({}) });
      await apiRequest(`/api/v1/clock/adjustments/${adjOut.id}/approve`, { method: "PATCH", body: JSON.stringify({}) });
      setPendingAdjs((prev) => prev.filter((a) => a.id !== adjIn.id && a.id !== adjOut.id));
      setPendingCount((c) => Math.max(0, c - 2));
      toast.success("Add Time Record approved");
      void loadPendingApprovals();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to approve");
    }
  };

  const rejectPair = async (adjIn: PendingAdj, adjOut: PendingAdj, reason: string) => {
    if (!reason.trim()) { toast.error("Rejection reason is required"); return; }
    try {
      await apiRequest(`/api/v1/clock/adjustments/${adjIn.id}/reject`, { method: "PATCH", body: JSON.stringify({ reason }) });
      await apiRequest(`/api/v1/clock/adjustments/${adjOut.id}/reject`, { method: "PATCH", body: JSON.stringify({ reason }) });
      setPendingAdjs((prev) => prev.filter((a) => a.id !== adjIn.id && a.id !== adjOut.id));
      setPendingCount((c) => Math.max(0, c - 2));
      setRejectingId(null);
      toast.success("Request rejected");
      void loadPendingApprovals();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to reject");
    }
  };

  // ── Add Time Record ─────────────────────────────────────────────────────

  const openAddRecord = async () => {
    if (arMembers.length === 0) {
      try {
        const res = await apiRequest<{ data: { id: string; firstName: string; lastName: string; timezone?: string | null }[] }>("/api/v1/team-members");
        setArMembers(
          (res.data ?? [])
            .sort((a, b) => a.firstName.localeCompare(b.firstName) || a.lastName.localeCompare(b.lastName))
            .map((m) => ({ id: m.id, name: `${m.firstName} ${m.lastName}`, timezone: m.timezone ?? null }))
        );
      } catch { /* ignore — member list stays empty */ }
    }
    setArDate(today);
    setArClockIn("");
    setArClockOut("");
    setArBreakMins(30);
    setArNotes("");
    setArMemberId("");
    setArSearch("");
    setArDropdownOpen(false);
    setShowAddRecord(true);
  };

  const submitAddRecord = async () => {
    if (!arMemberId || !arDate || !arClockIn || !arClockOut) {
      toast.error("Employee, date, clock-in, and clock-out are all required");
      return;
    }
    const arMemberTz = arMembers.find(m => m.id === arMemberId)?.timezone ?? null;
    if (new Date(parseDatetimeLocalInTz(arClockOut, arMemberTz)) <= new Date(parseDatetimeLocalInTz(arClockIn, arMemberTz))) {
      toast.error("Clock-out must be after clock-in");
      return;
    }
    setSavingAr(true);
    try {
      await apiRequest("/api/v1/clock/entries/manual", {
        method: "POST",
        body: JSON.stringify({
          teamMemberId: arMemberId,
          workDate:     arDate,
          clockIn:      parseDatetimeLocalInTz(arClockIn, arMemberTz),
          clockOut:     parseDatetimeLocalInTz(arClockOut, arMemberTz),
          breakMinutes: arBreakMins !== "" ? Number(arBreakMins) : 0,
          notes:        arNotes.trim() || undefined,
        }),
      });
      toast.success("Time record created");
      setShowAddRecord(false);
      void loadEntries();
      if (tab === "timesheets") void loadTimesheets();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create record");
    } finally { setSavingAr(false); }
  };

  // ── Override modal ──────────────────────────────────────────────────────

  const openOverride = (entry: ClockEntry) => {
    setActiveEntry(entry);
    setNoteText(entry.notes || "");
  };

  const closeOverride = () => {
    setActiveEntry(null);
    setNoteText("");
  };

  const submitOverride = async () => {
    if (!activeEntry || !noteText.trim()) { toast.error("Note is required"); return; }
    setSaving(true);
    try {
      const res = await apiRequest<{ entry: ClockEntry }>(
        `/api/v1/clock/entries/${activeEntry.id}/override`,
        { method: "PATCH", body: JSON.stringify({ notes: noteText.trim() }) }
      );
      setEntries((prev) => prev.map((e) => e.id === res.entry.id ? { ...e, ...res.entry } : e));
      setTsEntries((prev) => prev.map((e) => e.id === res.entry.id ? { ...e, ...res.entry } : e));
      toast.success("Note saved");
      closeOverride();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed");
    } finally { setSaving(false); }
  };

  // ── Edit times modal ────────────────────────────────────────────────────

  const openEditTimes = (entry: ClockEntry) => {
    setEditEntry(entry);
    setEditClockIn(toDatetimeLocalInTz(entry.adjustedClockInAt ?? entry.clockInAt, entry.timezone));
    setEditClockOut(toDatetimeLocalInTz(entry.adjustedClockOutAt ?? entry.clockOutAt, entry.timezone));
    setEditBreakMins(entry.totalBreakMinutes > 0 ? Math.round(entry.totalBreakMinutes) : "");
    setEditReason("");
  };

  const closeEditTimes = () => {
    setEditEntry(null);
    setEditClockIn("");
    setEditClockOut("");
    setEditBreakMins("");
    setEditReason("");
  };

  const submitEditTimes = async () => {
    if (!editEntry) return;
    if (!editReason.trim()) { toast.error("Reason is required"); return; }
    if (!editClockIn) { toast.error("Clock-in time is required"); return; }

    if (editClockOut) {
      const inMs  = new Date(parseDatetimeLocalInTz(editClockIn,  editEntry.timezone)).getTime();
      const outMs = new Date(parseDatetimeLocalInTz(editClockOut, editEntry.timezone)).getTime();
      if (!Number.isFinite(inMs) || !Number.isFinite(outMs)) {
        toast.error("Please enter valid clock-in and clock-out dates and times");
        return;
      }
      if (outMs <= inMs) { toast.error("Clock-out must be after clock-in"); return; }
      const durationHours = (outMs - inMs) / 3_600_000;
      if (durationHours > 16) {
        toast.error(`Shift duration is ${durationHours.toFixed(1)} hours — please verify the dates and times`);
        return;
      }
    }

    setSavingEdit(true);
    try {
      // Only send clock-in/out if actually changed from what the modal opened with —
      // otherwise saving an edit meant only for the reason/break silently re-asserts
      // whatever's in these fields, which can overwrite a correction made elsewhere
      // since this modal was opened (e.g. a stale tab left open across days).
      const baselineClockInMs  = editEntry.adjustedClockInAt  ?? editEntry.clockInAt  ? new Date((editEntry.adjustedClockInAt ?? editEntry.clockInAt)!).getTime() : null;
      const baselineClockOutMs = editEntry.adjustedClockOutAt ?? editEntry.clockOutAt ? new Date((editEntry.adjustedClockOutAt ?? editEntry.clockOutAt)!).getTime() : null;
      const newClockInMs  = new Date(parseDatetimeLocalInTz(editClockIn, editEntry.timezone)).getTime();
      const newClockOutMs = editClockOut ? new Date(parseDatetimeLocalInTz(editClockOut, editEntry.timezone)).getTime() : null;

      const body: Record<string, string | number> = { reason: editReason.trim() };
      if (newClockInMs !== baselineClockInMs) {
        body.clockInAt = parseDatetimeLocalInTz(editClockIn, editEntry.timezone);
      }
      if (editClockOut && newClockOutMs !== baselineClockOutMs) {
        body.clockOutAt = parseDatetimeLocalInTz(editClockOut, editEntry.timezone);
      }
      if (editBreakMins !== "") body.breakMinutes = Number(editBreakMins);
      if (editEntry.updatedAt) body.expectedUpdatedAt = editEntry.updatedAt;

      const res = await apiRequest<{ entry: ClockEntry }>(
        `/api/v1/clock/entries/${editEntry.id}/edit-times`,
        { method: "PATCH", body: JSON.stringify(body) }
      );
      setEntries((prev) => prev.map((e) => e.id === res.entry.id ? { ...e, ...res.entry } : e));
      setTsEntries((prev) => prev.map((e) => e.id === res.entry.id ? { ...e, ...res.entry } : e));
      toast.success("Times updated");
      closeEditTimes();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to update times");
    } finally { setSavingEdit(false); }
  };

  // ── Derived stats ───────────────────────────────────────────────────────

  const clockedInNow = entries.filter((e) => !e.clockOutAt).length;
  const flaggedCount = entries.filter((e) => !["ok", "pending"].includes(e.flagStatus)).length;

  // ── Render ──────────────────────────────────────────────────────────────

  const toggleAdjSort = (col: "employee" | "date") => {
    if (adjSortCol === col) setAdjSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setAdjSortCol(col); setAdjSortDir(col === "date" ? "desc" : "asc"); }
  };
  const adjSortIcon = (col: "employee" | "date") =>
    adjSortCol !== col ? " ↕" : adjSortDir === "asc" ? " ↑" : " ↓";
  const prepAdjs = (list: PendingAdj[]) =>
    groupAdjustments(
      [...list]
        .filter((a) => !adjSearch || (a.memberName ?? "").toLowerCase().includes(adjSearch.toLowerCase()))
        .sort((a, b) => {
          const dir = adjSortDir === "asc" ? 1 : -1;
          if (adjSortCol === "employee")
            return dir * (a.memberName ?? "").localeCompare(b.memberName ?? "");
          return dir * (a.workDate > b.workDate ? 1 : a.workDate < b.workDate ? -1 : 0);
        })
    );

  return (
    <div className="space-y-5">

      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <Clock className="h-6 w-6 text-blue-500" /> Clock Manager
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Monitor attendance and review approval requests.</p>
        </div>
        <button
          onClick={() => void loadEntries(true)}
          disabled={refreshing}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50 transition"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard icon={<Users className="h-5 w-5 text-green-600" />} bg="bg-green-100" label="Clocked In" value={clockedInNow} />
        {coverage && (
          <SummaryCard
            icon={<Users className={`h-5 w-5 ${coverage.gap < 0 ? "text-red-600" : "text-green-600"}`} />}
            bg={coverage.gap < 0 ? "bg-red-100" : "bg-green-100"}
            label="Coverage"
            value={`${coverage.clockedIn}/${coverage.scheduled}`}
            sub={coverage.gap < 0 ? `${coverage.gap} short` : undefined}
            subColor="text-red-500"
          />
        )}
        <SummaryCard icon={<AlertTriangle className="h-5 w-5 text-yellow-600" />} bg="bg-yellow-100" label="Flagged" value={flaggedCount} />
        <button
          onClick={() => setTab("approvals")}
          className="bg-white rounded-lg shadow p-4 flex items-center gap-3 hover:bg-yellow-50 transition text-left w-full"
        >
          <div className={`h-10 w-10 rounded-full flex items-center justify-center ${pendingCount > 0 ? "bg-yellow-100" : "bg-gray-100"}`}>
            <Hourglass className={`h-5 w-5 ${pendingCount > 0 ? "text-yellow-600" : "text-gray-400"}`} />
          </div>
          <div>
            <p className="text-xs text-gray-500">Time Adjustments</p>
            <p className={`text-2xl font-bold ${pendingCount > 0 ? "text-yellow-600" : "text-gray-800"}`}>{pendingCount}</p>
          </div>
        </button>
        <button
          onClick={() => { void loadMissingToday(); setTab("missing"); }}
          className="bg-white rounded-lg shadow p-4 flex items-center gap-3 hover:bg-red-50 transition text-left w-full"
        >
          <div className={`h-10 w-10 rounded-full flex items-center justify-center ${missing.length > 0 ? "bg-red-100" : "bg-gray-100"}`}>
            <UserX className={`h-5 w-5 ${missing.length > 0 ? "text-red-600" : "text-gray-400"}`} />
          </div>
          <div>
            <p className="text-xs text-gray-500">Not Clocked In</p>
            <p className={`text-2xl font-bold ${missing.length > 0 ? "text-red-600" : "text-gray-800"}`}>{missing.length}</p>
          </div>
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-0">
              {(
            [
              { key: "attendance",        label: "Attendance" },
              { key: "approvals",         label: "Time Adjustments", badge: pendingCount,    badgeColor: "bg-yellow-500" },
              { key: "time-off-approvals", label: "Time Off Requests", badge: toPendingCount, badgeColor: "bg-blue-500" },
              { key: "missing",           label: "Not Clocked In",  badge: missing.length,  badgeColor: "bg-red-500" },
              { key: "timesheets",        label: "Timesheets" },
              { key: "errors",            label: "Clock-In Errors", badge: errorCount,      badgeColor: "bg-red-600" },
            ] as { key: Tab; label: string; badge?: number; badgeColor?: string }[]
          ).map(({ key, label, badge, badgeColor }) => (
            <button
              key={key}
              onClick={() => {
                setTab(key);
                if (key === "missing") void loadMissingToday();
              }}
              className={`px-5 py-2.5 text-sm font-medium border-b-2 transition -mb-px flex items-center gap-2 ${
                tab === key
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              {label}
              {badge !== undefined && badge > 0 && (
                <span className={`inline-flex items-center justify-center min-w-[1.25rem] px-1.5 py-0.5 rounded-full text-white text-xs font-bold leading-none ${badgeColor}`}>
                  {badge}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* ── ATTENDANCE TAB ─────────────────────────────────────────────── */}
      {tab === "attendance" && (
        <div className="space-y-4">

          {/* Filters */}
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex flex-wrap gap-3 items-end">
              <div>
                <label className="block text-xs text-gray-500 mb-1">From</label>
                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                  className="rounded-md border border-gray-300 text-sm px-2 py-1.5" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">To</label>
                <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                  className="rounded-md border border-gray-300 text-sm px-2 py-1.5" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Exception</label>
                <select value={filterFlag} onChange={(e) => setFilterFlag(e.target.value)}
                  className="rounded-md border border-gray-300 text-sm px-2 py-1.5">
                  <option value="">All</option>
                  {Object.entries(FLAG_CONFIG).map(([k, v]) => (
                    <option key={k} value={k}>{v.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Search Employee</label>
                <input type="text" value={attSearch} onChange={(e) => setAttSearch(e.target.value)}
                  placeholder="First name..."
                  className="rounded-md border border-gray-300 text-sm px-2 py-1.5 w-36 focus:outline-none focus:ring-2 focus:ring-blue-400" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Region</label>
                <select value={attRegion} onChange={(e) => setAttRegion(e.target.value)}
                  className="rounded-md border border-gray-300 text-sm px-2 py-1.5">
                  <option value="">All Regions</option>
                  {tsRegions.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Role</label>
                <select value={attRole} onChange={(e) => setAttRole(e.target.value)}
                  className="rounded-md border border-gray-300 text-sm px-2 py-1.5">
                  <option value="">All Roles</option>
                  <option value="pcc_incoming">PCC - Incoming</option>
                  <option value="pcc_outbound">PCC - Outbound</option>
                  <option value="team_lead">Team Lead</option>
                  <option value="onboarding">Onboarding</option>
                  <option value="operations_manager">Operations Manager</option>
                  <option value="kpi">KPI</option>
                  <option value="upper_management">Upper Management</option>
                  <option value="qa">QA</option>
                  <option value="outbound_mod">Outbound MOD</option>
                  <option value="sales_marketing">Sales/Marketing</option>
                  <option value="tech_dev_support">Tech/Dev Support</option>
                  <option value="hr">HR</option>
                  <option value="accounting">Accounting</option>
                </select>
              </div>
              <button
                onClick={() => void loadEntries()}
                className="px-4 py-1.5 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
              >
                Apply
              </button>
              {(filterFlag || startDate !== today || endDate !== today || attSearch || attRegion || attRole) && (
                <button
                  onClick={() => { setStartDate(today); setEndDate(today); setFilterFlag(""); setAttSearch(""); setAttRegion(""); setAttRole(""); }}
                  className="px-3 py-1.5 rounded-md border border-gray-300 text-sm text-gray-600 hover:bg-gray-50"
                >
                  Reset
                </button>
              )}
              <div className="ml-auto flex items-center gap-2">
                <button
                  onClick={exportAttendance}
                  disabled={entries.length === 0}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-green-600 text-white text-sm font-medium hover:bg-green-700 disabled:opacity-40 transition"
                >
                  <Download className="h-4 w-4" /> Export CSV
                </button>
                <button
                  onClick={() => void openAddRecord()}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 transition"
                >
                  <PlusCircle className="h-4 w-4" /> Add Time Record
                </button>
              </div>
            </div>
          </div>

          {/* Entries table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {loading ? (
              <div className="p-8 text-center text-gray-400 text-sm">Loading attendance...</div>
            ) : entries.length === 0 ? (
              <div className="p-8 text-center text-gray-400 text-sm">No entries for this date range.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200 text-left text-xs text-gray-500 uppercase tracking-wide">
                      <th className="px-4 py-3 font-semibold">Employee</th>
                      <th className="px-4 py-3 font-semibold">Date</th>
                      <th className="px-4 py-3 font-semibold">Clock In</th>
                      <th className="px-4 py-3 font-semibold">Clock Out</th>
                      <th className="px-4 py-3 font-semibold">Worked</th>
                      <th className="px-4 py-3 font-semibold">Schedule</th>
                      <th className="px-4 py-3 font-semibold">Exceptions</th>
                      <th className="px-4 py-3 font-semibold w-24"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {entries.filter((entry) => {
                      if (attSearch && !(entry.memberFirstName ?? entry.memberName?.split(" ")[0] ?? "").toLowerCase().startsWith(attSearch.toLowerCase())) return false;
                      if (attRegion && entry.regionId !== attRegion) return false;
                      if (attRole && entry.role !== attRole) return false;
                      return true;
                    }).map((entry) => {
                      const inFlag     = entry.clockInFlag  ?? entry.flagStatus;
                      const outFlag    = entry.clockOutFlag ?? null;
                      const exceptions = [inFlag, outFlag]
                        .filter(Boolean)
                        .filter((f) => f !== "ok" && f !== "pending") as string[];
                      const isActive   = !entry.clockOutAt;
                      const effectiveIn  = entry.adjustedClockInAt  ?? entry.clockInAt;
                      const effectiveOut = entry.adjustedClockOutAt ?? entry.clockOutAt;

                      return (
                        <tr key={entry.id} className={`transition ${isActive ? "bg-green-50/40" : "hover:bg-gray-50"}`}>
                          <td className="px-4 py-3">
                            <div className="font-medium text-gray-900">{entry.memberName ?? "—"}</div>
                            {isActive && (
                              <span className="text-xs text-green-600 font-medium">● Active</span>
                            )}
                            {(entry.pendingAdjustments ?? 0) > 0 && (
                              <span className="ml-1.5 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">
                                {entry.pendingAdjustments} adj
                              </span>
                            )}
                            {entry.overrideReason && (
                              <div className="mt-0.5 text-xs text-purple-600 italic">"{entry.overrideReason}"</div>
                            )}
                            {(entry as any).scheduleOverrideType && (
                              <span className="inline-block mt-0.5 text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">
                                {(entry as any).scheduleOverrideType.replace("_", " ")}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-gray-600 text-xs">{entry.workDate}</td>
                          <td className="px-4 py-3">
                            <div className="text-gray-800 font-medium">{fmt(effectiveIn, entry.timezone)}</div>
                            {entry.adjustedClockInAt && entry.clockInAt && (
                              <div className="text-xs text-gray-400">Actual: {fmt(entry.clockInAt, entry.timezone)}</div>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-gray-800 font-medium">{fmt(effectiveOut, entry.timezone)}</div>
                            {entry.adjustedClockOutAt && entry.clockOutAt && (
                              <div className="text-xs text-gray-400">Actual: {fmt(entry.clockOutAt, entry.timezone)}</div>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-gray-700">{fmtMins(entry.totalWorkedMinutes)}</div>
                            {(entry.totalOvertimeMinutes ?? 0) > 0 && (
                              <div className="text-xs text-red-500">+{fmtMins(entry.totalOvertimeMinutes)} OT</div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-500">
                            {entry.scheduledStart && entry.scheduledEnd
                              ? `${entry.scheduledStart} – ${entry.scheduledEnd}`
                              : "—"}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                              {exceptions.length === 0 ? (
                                <span className="text-xs text-gray-300">—</span>
                              ) : exceptions.map((f) => {
                                const cfg = FLAG_CONFIG[f];
                                return cfg ? (
                                  <span key={f} className={`px-1.5 py-0.5 rounded text-xs font-medium ${cfg.bg} ${cfg.text}`}>
                                    {cfg.label}
                                  </span>
                                ) : null;
                              })}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-col gap-1.5">
                              <button
                                onClick={() => openEditTimes(entry)}
                                className="inline-flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800 font-medium"
                              >
                                <Edit2 className="h-3 w-3" />
                                Edit Times
                              </button>
                              <button
                                onClick={() => openOverride(entry)}
                                className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
                              >
                                <Edit2 className="h-3 w-3" />
                                {entry.overrideReason ? "Edit Note" : "Add Note"}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── APPROVALS TAB ──────────────────────────────────────────────── */}
      {tab === "approvals" && (
        <div className="space-y-6">
          <div className="flex items-center justify-between gap-3">
            <input
              type="text"
              value={adjSearch}
              onChange={(e) => setAdjSearch(e.target.value)}
              placeholder="Search by employee name..."
              className="w-56 rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <button
              onClick={exportApprovals}
              disabled={pendingAdjs.length === 0 && resolvedAdjs.length === 0}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 disabled:opacity-40 transition"
            >
              <Download className="h-4 w-4" /> Export CSV
            </button>
          </div>
          {/* Pending section */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <Hourglass className="h-4 w-4 text-yellow-500" />
              Pending Review
              {pendingAdjs.length > 0 && (
                <span className="bg-yellow-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                  {prepAdjs(pendingAdjs).length}
                </span>
              )}
            </h3>
            {prepAdjs(pendingAdjs).length === 0 ? (
              <div className="bg-white rounded-lg shadow p-8 text-center">
                <CheckCircle className="h-8 w-8 text-green-400 mx-auto mb-2" />
                <p className="text-sm font-medium text-gray-700">{adjSearch ? "No matching requests" : "All caught up"}</p>
                <p className="text-xs text-gray-400 mt-1">{adjSearch ? `No pending requests for "${adjSearch}".` : "No pending requests right now."}</p>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200 text-left text-xs text-gray-500 uppercase tracking-wide">
                      <th className="px-4 py-3 font-semibold cursor-pointer select-none hover:text-gray-800" onClick={() => toggleAdjSort("employee")}>Employee{adjSortIcon("employee")}</th>
                      <th className="px-4 py-3 font-semibold cursor-pointer select-none hover:text-gray-800" onClick={() => toggleAdjSort("date")}>Date{adjSortIcon("date")}</th>
                      <th className="px-4 py-3 font-semibold">Type</th>
                      <th className="px-4 py-3 font-semibold">Details</th>
                      <th className="px-4 py-3 font-semibold">Reason</th>
                      <th className="px-4 py-3 font-semibold text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {prepAdjs(pendingAdjs).flatMap((group) => {
                      const isRejecting = rejectingId === group.key;
                      const rejectRow = isRejecting ? (
                        <tr key={`reject-${group.key}`} className="bg-red-50">
                          <td colSpan={6} className="px-4 py-3">
                            <RejectReasonInput
                              onConfirm={(reason) => group.kind === "missed_day" ? void rejectPair(group.adjIn, group.adjOut, reason) : void rejectPendingAdj(group.adj, reason)}
                              onCancel={() => setRejectingId(null)}
                            />
                          </td>
                        </tr>
                      ) : null;

                      if (group.kind === "missed_day") {
                        const { adjIn, adjOut } = group;
                        const inMismatch  = dateMismatch(adjIn.requestedValue, adjIn.workDate, adjIn.timezone);
                        const outMismatch = dateMismatch(adjOut.requestedValue, adjIn.workDate, adjOut.timezone);
                        const inTime  = fmtFull(adjIn.requestedValue, adjIn.timezone);
                        const outTime = fmtFull(adjOut.requestedValue, adjOut.timezone);
                        const brk     = adjBreakMins(adjOut);
                        const rows: JSX.Element[] = [
                          <tr key={group.key} className="hover:bg-gray-50">
                            <td className="px-4 py-3 font-medium text-gray-900">{adjIn.memberName ?? "—"}</td>
                            <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">{adjIn.workDate}</td>
                            <td className="px-4 py-3">
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                                <PlusCircle className="h-3 w-3" /> Add Time Record
                              </span>
                            </td>
                            <td className="px-4 py-3 text-xs">
                              <div className="font-medium text-gray-800">
                                <span className={inMismatch ? "text-red-600 font-bold" : ""}>{inTime}</span>
                                {" → "}
                                <span className={outMismatch ? "text-red-600 font-bold" : ""}>{outTime}</span>
                              </div>
                              {(inMismatch || outMismatch) && (() => {
                                // Compare the clock-out against the requested clock-in (no existing entry
                                // to reference yet — both times are being requested together).
                                const explanation = outMismatch
                                  ? explainDateMismatch(adjOut.requestedValue, adjIn.requestedValue, adjIn.workDate)
                                  : explainDateMismatch(adjIn.requestedValue, null, adjIn.workDate);
                                const colorClass =
                                  explanation.severity === "impossible" ? "text-red-600" :
                                  explanation.severity === "long"       ? "text-red-500" :
                                                                            "text-amber-600";
                                return (
                                  <div className={`mt-0.5 font-semibold flex items-start gap-1 ${colorClass}`}>
                                    <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                                    <span>{explanation.text}</span>
                                  </div>
                                );
                              })()}
                              {brk > 0 && <div className="text-gray-500 mt-0.5">Break: {brk}m</div>}
                              {(adjIn.scheduledStart && adjIn.scheduledEnd) && (
                                <div className="mt-1 text-blue-600 font-medium">Sched: {adjIn.scheduledStart} – {adjIn.scheduledEnd}</div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-500 max-w-[180px]">
                              <span className="italic">"{adjIn.reason}"</span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              {!isRejecting && (
                                <div className="inline-flex items-center gap-2">
                                  <button onClick={() => void approvePair(adjIn, adjOut)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-green-600 text-white text-xs font-semibold hover:bg-green-700 transition">
                                    <ThumbsUp className="h-3.5 w-3.5" /> Approve
                                  </button>
                                  <button onClick={() => setRejectingId(group.key)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-red-300 text-red-600 text-xs font-semibold hover:bg-red-50 transition">
                                    <ThumbsDown className="h-3.5 w-3.5" /> Reject
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>,
                        ];
                        if (rejectRow) rows.push(rejectRow);
                        return rows;
                      }

                      const { adj } = group;
                      const isBreakAdj = adj.adjustmentType === "incorrect_break" || adj.adjustmentType === "missed_break";
                      const reqMismatch = !isBreakAdj && dateMismatch(adj.requestedValue, adj.workDate, adj.timezone);
                      const origMismatch = !isBreakAdj && !!adj.originalValue && dateMismatch(adj.originalValue, adj.workDate, adj.timezone);
                      const reqTime  = isBreakAdj
                        ? `${Math.round(new Date(adj.requestedValue).getTime() / 60000)} minutes`
                        : fmtFull(adj.requestedValue, adj.timezone);
                      const origTime = adj.originalValue && adj.adjustmentType !== "missed_clock_out"
                        ? fmtFull(adj.originalValue, adj.timezone)
                        : null;
                      const rows: JSX.Element[] = [
                        <tr key={group.key} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium text-gray-900">{adj.memberName ?? "—"}</td>
                          <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">{adj.workDate}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${adjBadgeColor(adj.adjustmentType)}`}>
                              <Hourglass className="h-3 w-3" /> {adjTypeLabel(adj.adjustmentType, adj.field)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs">
                            {origTime && !isBreakAdj && (
                              <div className={origMismatch ? "text-red-600 font-bold" : "text-gray-400"}>Was: {origTime}</div>
                            )}
                            <div className="font-medium text-gray-800">
                              → <span className={reqMismatch ? "text-red-600 font-bold" : ""}>{reqTime}</span>
                            </div>
                            {(reqMismatch || origMismatch) && (() => {
                              const explanation = explainDateMismatch(
                                reqMismatch ? adj.requestedValue : adj.originalValue!,
                                adj.clockInAt,
                                adj.workDate
                              );
                              const colorClass =
                                explanation.severity === "impossible" ? "text-red-600" :
                                explanation.severity === "long"       ? "text-red-500" :
                                                                          "text-amber-600";
                              return (
                                <div className={`mt-0.5 font-semibold flex items-start gap-1 ${colorClass}`}>
                                  <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                                  <span>{explanation.text}</span>
                                </div>
                              );
                            })()}
                            {(adj.scheduledStart && adj.scheduledEnd) && (
                              <div className="mt-1.5 pt-1.5 border-t border-gray-100">
                                <div className="text-blue-600 font-medium">Sched: {adj.scheduledStart} – {adj.scheduledEnd}</div>
                                {adj.clockInAt && (
                                  <div className="text-gray-500 mt-0.5">
                                    Clocked in: {new Date(adj.clockInAt).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", timeZone: adj.timezone ?? DISPLAY_TZ })}
                                  </div>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-500 max-w-[180px]">
                            <span className="italic">"{adj.reason}"</span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            {!isRejecting && (
                              <div className="inline-flex items-center gap-2">
                                <button onClick={() => void approvePendingAdj(adj)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-green-600 text-white text-xs font-semibold hover:bg-green-700 transition">
                                  <ThumbsUp className="h-3.5 w-3.5" /> Approve
                                </button>
                                <button onClick={() => setRejectingId(group.key)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-red-300 text-red-600 text-xs font-semibold hover:bg-red-50 transition">
                                  <ThumbsDown className="h-3.5 w-3.5" /> Reject
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>,
                      ];
                      if (rejectRow) rows.push(rejectRow);
                      return rows;
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Resolved history section */}
          <div>
            <h3 className="text-sm font-semibold text-gray-500 mb-3 flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-gray-400" />
              Recent History (last 30 days)
            </h3>
            {resolvedAdjs.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-6 text-center">
                <p className="text-xs text-gray-400">No resolved requests in the last 30 days.</p>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200 text-left text-xs text-gray-500 uppercase tracking-wide">
                      <th className="px-4 py-3 font-semibold cursor-pointer select-none hover:text-gray-800" onClick={() => toggleAdjSort("employee")}>Employee{adjSortIcon("employee")}</th>
                      <th className="px-4 py-3 font-semibold cursor-pointer select-none hover:text-gray-800" onClick={() => toggleAdjSort("date")}>Date{adjSortIcon("date")}</th>
                      <th className="px-4 py-3 font-semibold">Type</th>
                      <th className="px-4 py-3 font-semibold">Details</th>
                      <th className="px-4 py-3 font-semibold">Status</th>
                      <th className="px-4 py-3 font-semibold text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {prepAdjs(resolvedAdjs).map((group) => {
                      if (group.kind === "missed_day") {
                        const { adjIn, adjOut } = group;
                        const inTime   = fmtFull(adjIn.requestedValue, adjIn.timezone);
                        const outTime  = fmtFull(adjOut.requestedValue, adjOut.timezone);
                        const brk      = adjBreakMins(adjOut);
                        const approved = adjIn.status === "approved";
                        return (
                          <tr key={group.key} className="hover:bg-gray-50">
                            <td className="px-4 py-3 font-medium text-gray-900">{adjIn.memberName ?? "—"}</td>
                            <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">{adjIn.workDate}</td>
                            <td className="px-4 py-3">
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                                <PlusCircle className="h-3 w-3" /> Add Time Record
                              </span>
                            </td>
                            <td className="px-4 py-3 text-xs">
                              <div className="font-medium text-gray-800">{inTime} → {outTime}</div>
                              {brk > 0 && <div className="text-gray-500 mt-0.5">Break: {brk}m</div>}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold ${approved ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                                {approved ? "Approved" : "Rejected"}
                              </span>
                              {!approved && adjIn.rejectionReason && (
                                <p className="text-xs text-red-500 mt-1 italic">"{adjIn.rejectionReason}"</p>
                              )}
                              {adjIn.resolvedAt && (
                                <p className="text-xs text-gray-400 mt-0.5">{new Date(adjIn.resolvedAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</p>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <button
                                onClick={() => { void retractAdj(adjIn); void retractAdj(adjOut); }}
                                disabled={retractingId === adjIn.id || retractingId === adjOut.id}
                                className="text-xs text-gray-500 hover:text-gray-700 border border-gray-300 px-2 py-1 rounded disabled:opacity-40"
                              >↩ Retract</button>
                            </td>
                          </tr>
                        );
                      }

                      const { adj } = group;
                      const isBreakAdj2 = adj.adjustmentType === "incorrect_break" || adj.adjustmentType === "missed_break";
                      const reqTime  = isBreakAdj2
                        ? `${Math.round(new Date(adj.requestedValue).getTime() / 60000)} minutes`
                        : fmtFull(adj.requestedValue, adj.timezone);
                      const origTime = adj.originalValue && adj.adjustmentType !== "missed_clock_out"
                        ? fmtFull(adj.originalValue, adj.timezone)
                        : null;
                      const approved = adj.status === "approved";
                      return (
                        <tr key={group.key} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium text-gray-900">{adj.memberName ?? "—"}</td>
                          <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">{adj.workDate}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${adjBadgeColor(adj.adjustmentType)}`}>
                              <Hourglass className="h-3 w-3" /> {adjTypeLabel(adj.adjustmentType, adj.field)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs">
                            {origTime && !isBreakAdj2 && <div className="text-gray-400">Was: {origTime}</div>}
                            <div className="font-medium text-gray-800">→ {reqTime}</div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold ${approved ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                              {approved ? "Approved" : "Rejected"}
                            </span>
                            {!approved && adj.rejectionReason && (
                              <p className="text-xs text-red-500 mt-1 italic">"{adj.rejectionReason}"</p>
                            )}
                            {adj.resolvedAt && (
                              <p className="text-xs text-gray-400 mt-0.5">{new Date(adj.resolvedAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</p>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              onClick={() => void retractAdj(adj)}
                              disabled={retractingId === adj.id}
                              className="text-xs text-gray-500 hover:text-gray-700 border border-gray-300 px-2 py-1 rounded disabled:opacity-40"
                            >{retractingId === adj.id ? "..." : "↩ Retract"}</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── TIME OFF APPROVALS TAB ─────────────────────────────────────── */}
      {tab === "time-off-approvals" && (
        <div className="space-y-6">

          {/* Pending requests */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <Hourglass className="h-4 w-4 text-blue-500" />
              Pending Requests
              {toPendingCount > 0 && (
                <span className="bg-blue-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">{toPendingCount}</span>
              )}
            </h3>

            {toRequests.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-8 text-center">
                <Umbrella className="h-8 w-8 text-green-400 mx-auto mb-2" />
                <p className="text-sm font-medium text-gray-700">No pending time-off requests</p>
                <p className="text-xs text-gray-400 mt-1">All caught up — no requests awaiting review.</p>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200 text-left text-xs text-gray-500 uppercase tracking-wide">
                      <th className="px-4 py-3 font-semibold">Employee</th>
                      <th className="px-4 py-3 font-semibold">Dates</th>
                      <th className="px-4 py-3 font-semibold">Type</th>
                      <th className="px-4 py-3 font-semibold">Reason</th>
                      <th className="px-4 py-3 font-semibold">Submitted</th>
                      <th className="px-4 py-3 font-semibold text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {toRequests.map((r) => {
                      const isRejecting  = toRejectingId === r.id;
                      const isApproving  = toApprovingId === r.id;
                      const days = Math.round(
                        (new Date(r.endDate + "T00:00:00").getTime() - new Date(r.startDate + "T00:00:00").getTime()) / 86400000
                      ) + 1;
                      const dateRange = r.startDate === r.endDate
                        ? new Date(r.startDate + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                        : `${new Date(r.startDate + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${new Date(r.endDate + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
                      const typeLabel = r.requestType === "time_off" ? "Time Off" : r.requestType === "sick" ? "Sick Day" : "Personal";

                      return (
                        <>
                          <tr key={r.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 font-medium text-gray-900">{r.memberName}</td>
                            <td className="px-4 py-3 text-gray-700 text-xs whitespace-nowrap">
                              <div className="font-medium">{dateRange}</div>
                              <div className="text-gray-400 mt-0.5">{days} day{days !== 1 ? "s" : ""}</div>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
                                r.requestType === "sick"     ? "bg-red-100 text-red-700" :
                                r.requestType === "personal" ? "bg-purple-100 text-purple-700" :
                                                               "bg-blue-100 text-blue-700"
                              }`}>
                                <Umbrella className="h-3 w-3" /> {typeLabel}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-500 max-w-[200px]">
                              {r.reason ? <span className="italic">"{r.reason}"</span> : <span className="text-gray-300">—</span>}
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">
                              {new Date(r.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                            </td>
                            <td className="px-4 py-3 text-right">
                              {!isRejecting && !isApproving && (
                                <div className="inline-flex items-center gap-2">
                                  <button
                                    onClick={() => setToApprovingId(r.id)}
                                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-green-600 text-white text-xs font-semibold hover:bg-green-700 transition"
                                  >
                                    <ThumbsUp className="h-3.5 w-3.5" /> Approve
                                  </button>
                                  <button
                                    onClick={() => setToRejectingId(r.id)}
                                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-red-300 text-red-600 text-xs font-semibold hover:bg-red-50 transition"
                                  >
                                    <ThumbsDown className="h-3.5 w-3.5" /> Reject
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>

                          {/* Inline approve row — optional note before confirming */}
                          {isApproving && (
                            <tr key={`approve-${r.id}`} className="bg-green-50">
                              <td colSpan={6} className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                  <input
                                    type="text"
                                    autoFocus
                                    placeholder="Note to employee (optional)..."
                                    value={toApproveNoteMap[r.id] ?? ""}
                                    onChange={(e) => setToApproveNoteMap((m) => ({ ...m, [r.id]: e.target.value }))}
                                    className="flex-1 rounded-md border border-gray-300 text-xs px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-green-400"
                                  />
                                  <button
                                    onClick={async () => {
                                      try {
                                        await apiRequest(`/api/v1/time-off/${r.id}/approve`, {
                                          method: "PATCH",
                                          body: JSON.stringify({ reviewNote: toApproveNoteMap[r.id]?.trim() || null }),
                                        });
                                        toast.success("Time-off request approved");
                                        setToApprovingId(null);
                                        void loadTimeOffApprovals();
                                      } catch (err: any) {
                                        if (err?.conflicts) {
                                          toast.error(`Conflicts on: ${(err.conflicts as {date:string}[]).map(c => c.date).join(", ")}`);
                                        } else {
                                          toast.error(err?.message ?? "Failed to approve");
                                        }
                                        setToApprovingId(null);
                                      }
                                    }}
                                    className="px-3 py-1.5 rounded-md bg-green-600 text-white text-xs font-semibold hover:bg-green-700"
                                  >
                                    Confirm
                                  </button>
                                  <button onClick={() => setToApprovingId(null)} className="px-3 py-1.5 rounded-md border border-gray-300 text-xs text-gray-600 hover:bg-gray-50">Cancel</button>
                                </div>
                              </td>
                            </tr>
                          )}

                          {/* Inline reject row — note required */}
                          {isRejecting && (
                            <tr key={`reject-${r.id}`} className="bg-red-50">
                              <td colSpan={6} className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                  <input
                                    type="text"
                                    autoFocus
                                    placeholder="Reason for rejection (required)..."
                                    value={toRejectNote[r.id] ?? ""}
                                    onChange={(e) => setToRejectNote((m) => ({ ...m, [r.id]: e.target.value }))}
                                    className="flex-1 rounded-md border border-gray-300 text-xs px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-red-400"
                                  />
                                  <button
                                    onClick={async () => {
                                      const note = toRejectNote[r.id]?.trim();
                                      if (!note) { toast.error("Rejection reason is required"); return; }
                                      try {
                                        await apiRequest(`/api/v1/time-off/${r.id}/reject`, {
                                          method: "PATCH",
                                          body: JSON.stringify({ reviewNote: note }),
                                        });
                                        toast.success("Time-off request rejected");
                                        setToRejectingId(null);
                                        void loadTimeOffApprovals();
                                      } catch (err: any) {
                                        toast.error(err?.message ?? "Failed to reject");
                                        setToRejectingId(null);
                                      }
                                    }}
                                    className="px-3 py-1.5 rounded-md bg-red-600 text-white text-xs font-semibold hover:bg-red-700"
                                  >
                                    Confirm
                                  </button>
                                  <button onClick={() => setToRejectingId(null)} className="px-3 py-1.5 rounded-md border border-gray-300 text-xs text-gray-600 hover:bg-gray-50">Cancel</button>
                                </div>
                              </td>
                            </tr>
                          )}
                        </>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Recent resolved */}
          <div>
            <h3 className="text-sm font-semibold text-gray-500 mb-3 flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-gray-400" />
              Recently Approved (last 30 days)
            </h3>
            {toHistory.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-6 text-center">
                <p className="text-xs text-gray-400">No approved time-off in the last 30 days.</p>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200 text-left text-xs text-gray-500 uppercase tracking-wide">
                      <th className="px-4 py-3 font-semibold">Employee</th>
                      <th className="px-4 py-3 font-semibold">Dates</th>
                      <th className="px-4 py-3 font-semibold">Type</th>
                      <th className="px-4 py-3 font-semibold">Note</th>
                      <th className="px-4 py-3 font-semibold">Status</th>
                      <th className="px-4 py-3 font-semibold text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {toHistory.map((r) => {
                      const days = Math.round(
                        (new Date(r.endDate + "T00:00:00").getTime() - new Date(r.startDate + "T00:00:00").getTime()) / 86400000
                      ) + 1;
                      const dateRange = r.startDate === r.endDate
                        ? new Date(r.startDate + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                        : `${new Date(r.startDate + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${new Date(r.endDate + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
                      const typeLabel = r.requestType === "time_off" ? "Time Off" : r.requestType === "sick" ? "Sick Day" : "Personal";
                      return (
                        <tr key={r.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium text-gray-900">{r.memberName}</td>
                          <td className="px-4 py-3 text-xs text-gray-700 whitespace-nowrap">
                            <div>{dateRange}</div>
                            <div className="text-gray-400 mt-0.5">{days} day{days !== 1 ? "s" : ""}</div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
                              r.requestType === "sick"     ? "bg-red-100 text-red-700" :
                              r.requestType === "personal" ? "bg-purple-100 text-purple-700" :
                                                             "bg-blue-100 text-blue-700"
                            }`}>
                              <Umbrella className="h-3 w-3" /> {typeLabel}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-400 max-w-[180px]">
                            {r.reviewNote ?? "—"}
                          </td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                              <CheckCircle className="h-3 w-3" /> Approved
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              disabled={toRevertingId === r.id}
                              onClick={async () => {
                                setToRevertingId(r.id);
                                try {
                                  await apiRequest(`/api/v1/time-off/${r.id}/revert`, { method: "PATCH" });
                                  toast.success("Reverted — request moved back to pending");
                                  void loadTimeOffApprovals();
                                } catch (err: any) {
                                  toast.error(err?.message ?? "Failed to revert");
                                } finally {
                                  setToRevertingId(null);
                                }
                              }}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 text-xs font-semibold hover:bg-gray-50 disabled:opacity-40 transition"
                            >
                              <RotateCcw className="h-3.5 w-3.5" /> Revert
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── NOT CLOCKED IN TAB ─────────────────────────────────────────── */}
      {tab === "missing" && (
        <div className="space-y-4">
          <div className="flex justify-end gap-2">
            <button
              onClick={() => void loadMissingToday()}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 text-sm text-gray-600 hover:bg-gray-50 transition"
            >
              <RefreshCw className="h-4 w-4" /> Refresh
            </button>
            <button
              onClick={exportMissing}
              disabled={missing.length === 0}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 disabled:opacity-40 transition"
            >
              <Download className="h-4 w-4" /> Export CSV
            </button>
          </div>
          {coverage && (
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white rounded-lg shadow p-4 text-center">
                <p className="text-xs text-gray-500 mb-1">Scheduled Today</p>
                <p className="text-2xl font-bold text-gray-800">{coverage.scheduled}</p>
              </div>
              <div className="bg-white rounded-lg shadow p-4 text-center">
                <p className="text-xs text-gray-500 mb-1">Clocked In</p>
                <p className="text-2xl font-bold text-green-600">{coverage.clockedIn}</p>
              </div>
              <div className="bg-white rounded-lg shadow p-4 text-center">
                <p className="text-xs text-gray-500 mb-1">Not Clocked In</p>
                <p className={`text-2xl font-bold ${missing.length > 0 ? "text-red-600" : "text-gray-800"}`}>{missing.length}</p>
              </div>
            </div>
          )}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {missing.length === 0 ? (
              <div className="p-10 text-center">
                <CheckCircle className="h-10 w-10 text-green-400 mx-auto mb-3" />
                <p className="text-sm font-medium text-gray-700">All scheduled employees are clocked in</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200 text-left text-xs text-gray-500 uppercase tracking-wide">
                    <th className="px-4 py-3 font-semibold">Employee</th>
                    <th className="px-4 py-3 font-semibold">Scheduled Start</th>
                    <th className="px-4 py-3 font-semibold">Time Since Start</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {missing.map((m) => (
                    <tr key={m.teamMemberId} className="hover:bg-red-50">
                      <td className="px-4 py-3 font-medium text-gray-900">{m.name}</td>
                      <td className="px-4 py-3 text-gray-600">{m.scheduledStart}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                          +{m.minutesLate}m late
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ── TIMESHEETS TAB ─────────────────────────────────────────────── */}
      {tab === "timesheets" && (
        <div className="space-y-4">

          {/* Filters */}
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex flex-wrap gap-3 items-end">
              <div>
                <label className="block text-xs text-gray-500 mb-1">From</label>
                <input type="date" value={tsStart} onChange={(e) => setTsStart(e.target.value)}
                  className="rounded-lg border border-gray-200 text-sm px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">To</label>
                <input type="date" value={tsEnd} onChange={(e) => setTsEnd(e.target.value)}
                  className="rounded-lg border border-gray-200 text-sm px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Search Employee</label>
                <input type="text" value={tsSearch} onChange={(e) => setTsSearch(e.target.value)}
                  placeholder="Name..." className="rounded-lg border border-gray-200 text-sm px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Region</label>
                <select value={tsRegion} onChange={(e) => setTsRegion(e.target.value)}
                  className="rounded-lg border border-gray-200 text-sm px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400">
                  <option value="">All Regions</option>
                  {tsRegions.map((r) => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Role</label>
                <select value={tsRole} onChange={(e) => setTsRole(e.target.value)}
                  className="rounded-lg border border-gray-200 text-sm px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400">
                  <option value="">All Roles</option>
                  <option value="pcc_incoming">PCC - Incoming</option>
                  <option value="pcc_outbound">PCC - Outbound</option>
                  <option value="team_lead">Team Lead</option>
                  <option value="onboarding">Onboarding</option>
                  <option value="operations_manager">Operations Manager</option>
                  <option value="kpi">KPI</option>
                  <option value="upper_management">Upper Management</option>
                  <option value="qa">QA</option>
                  <option value="outbound_mod">Outbound MOD</option>
                  <option value="sales_marketing">Sales/Marketing</option>
                  <option value="tech_dev_support">Tech/Dev Support</option>
                  <option value="hr">HR</option>
                  <option value="accounting">Accounting</option>
                </select>
              </div>
              <button onClick={() => void loadTimesheets()}
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition">
                <RefreshCw className="h-4 w-4" /> Load
              </button>
              <div className="ml-auto flex items-center gap-2">
                <button
                  onClick={() => void generateDuplicates()}
                  disabled={generatingDuplicates}
                  title="Scan the selected date range for likely duplicate entries (a real shift plus a 'Manually added by manager' copy) and download an Excel report"
                  className="inline-flex items-center gap-2 px-4 py-1.5 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-40 transition"
                >
                  <Copy className="h-4 w-4" /> {generatingDuplicates ? "Generating..." : "Generate Duplicates"}
                </button>
                <button
                  onClick={() => void openAddRecord()}
                  className="inline-flex items-center gap-2 px-4 py-1.5 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 transition"
                >
                  <PlusCircle className="h-4 w-4" /> Add Time Record
                </button>
                <button
                  onClick={exportTimesheets}
                  disabled={tsEntries.length === 0}
                  className="inline-flex items-center gap-2 px-4 py-1.5 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 disabled:opacity-40 transition"
                >
                  <Download className="h-4 w-4" /> Export CSV
                </button>
              </div>
            </div>
          </div>

          {tsLoading && tsEntries.length === 0 ? (
            <div className="text-center py-10 text-gray-400 text-sm">Loading...</div>
          ) : (() => {
            // Group by employee
            const byEmployee = new Map<string, { name: string; firstName: string; entries: ClockEntry[] }>();
            for (const e of tsEntries) {
              if (tsSearch && !(e.memberFirstName ?? e.memberName?.split(" ")[0] ?? "").toLowerCase().startsWith(tsSearch.toLowerCase())) continue;
              if (tsRegion && e.regionId !== tsRegion) continue;
              if (tsRole && e.role !== tsRole) continue;
              if (!byEmployee.has(e.teamMemberId)) {
                byEmployee.set(e.teamMemberId, {
                  name: e.memberName ?? e.teamMemberId,
                  firstName: e.memberFirstName ?? e.memberName?.split(" ")[0] ?? "",
                  entries: [],
                });
              }
              byEmployee.get(e.teamMemberId)!.entries.push(e);
            }
            const sortedEmployees = Array.from(byEmployee.entries()).sort(
              ([, a], [, b]) => a.firstName.localeCompare(b.firstName)
            );

            if (byEmployee.size === 0) {
              return (
                <div className="bg-white rounded-lg shadow p-10 text-center">
                  <FileSpreadsheet className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-gray-500">No completed shifts found for this period.</p>
                </div>
              );
            }

            return (
              <div className="space-y-3">
                {sortedEmployees.map(([memberId, { name, entries: empEntries }]) => {
                  const totalWorked   = empEntries.reduce((s, e) => s + (e.totalWorkedMinutes ?? 0), 0);
                  const totalOT       = empEntries.reduce((s, e) => s + (e.totalOvertimeMinutes ?? 0), 0);
                  const totalBreak    = empEntries.reduce((s, e) => s + e.totalBreakMinutes, 0);
                  const isOpen        = expanded.has(memberId);
                  const toggleExpand  = () => setExpanded((prev) => {
                    const next = new Set(prev);
                    isOpen ? next.delete(memberId) : next.add(memberId);
                    return next;
                  });

                  return (
                    <div key={memberId} className="bg-white rounded-xl shadow overflow-hidden">

                      {/* Employee summary row */}
                      <button
                        onClick={toggleExpand}
                        className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition text-left"
                      >
                        <div className="flex items-center gap-3">
                          {isOpen
                            ? <ChevronDown className="h-4 w-4 text-gray-400" />
                            : <ChevronRight className="h-4 w-4 text-gray-400" />}
                          <span className="font-semibold text-gray-900 text-sm">{name}</span>
                          <span className="text-xs text-gray-400">{empEntries.length} shift{empEntries.length !== 1 ? "s" : ""}</span>
                        </div>
                        <div className="flex items-center gap-6 text-sm">
                          <div className="text-right">
                            <p className="text-xs text-gray-400">Regular</p>
                            <p className="font-semibold text-gray-800">{fmtMins(totalWorked - totalOT)}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-gray-400">Overtime</p>
                            <p className={`font-semibold ${totalOT > 0 ? "text-red-600" : "text-gray-400"}`}>
                              {totalOT > 0 ? `+${fmtMins(totalOT)}` : "—"}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-gray-400">Total</p>
                            <p className="font-bold text-blue-700">{fmtMins(totalWorked)}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-gray-400">Break</p>
                            <p className="font-semibold text-gray-500">{fmtMins(totalBreak)}</p>
                          </div>
                        </div>
                      </button>

                      {/* Shift detail rows */}
                      {isOpen && (
                        <div className="border-t border-gray-100">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="bg-gray-50 text-gray-400 uppercase tracking-wide">
                                <th className="px-5 py-2 text-left font-semibold">Date</th>
                                <th className="px-4 py-2 text-left font-semibold">Clock In</th>
                                <th className="px-4 py-2 text-left font-semibold">Clock Out</th>
                                <th className="px-4 py-2 text-left font-semibold">Scheduled</th>
                                <th className="px-4 py-2 text-left font-semibold">Break</th>
                                <th className="px-4 py-2 text-left font-semibold">Worked</th>
                                <th className="px-4 py-2 text-left font-semibold">Overtime</th>
                                <th className="px-4 py-2 text-left font-semibold">Exceptions</th>
                                <th className="px-4 py-2 text-left font-semibold">Exclusions</th>
                                <th className="px-4 py-2 text-right font-semibold">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                              {empEntries
                                .slice()
                                .sort((a, b) => a.workDate > b.workDate ? -1 : 1)
                                .map((e) => {
                                  const effectiveIn  = e.adjustedClockInAt  ?? e.clockInAt;
                                  const effectiveOut = e.adjustedClockOutAt ?? e.clockOutAt;
                                  // "Working" only applies to a shift genuinely still open right
                                  // now — an entry with no clock-out that's older than a reasonable
                                  // max shift length (matches the backend's own
                                  // MAX_SHIFT_DURATION_HOURS / ClockPortal's stale-shift grace) is a
                                  // forgotten/broken record, not an active shift, and should read
                                  // as an exception rather than "Working".
                                  const hoursSinceClockIn = effectiveIn ? (Date.now() - new Date(effectiveIn).getTime()) / 3_600_000 : Infinity;
                                  const isInProgress = !e.isScheduledOnly && !!effectiveIn && !effectiveOut && hoursSinceClockIn < 16;
                                  const isStaleOpen = !e.isScheduledOnly && !!effectiveIn && !effectiveOut && hoursSinceClockIn >= 16;
                                  const inDev  = e.clockInDeviationMinutes;
                                  const outDev = e.clockOutDeviationMinutes;

                                  const exceptions: { label: string; color: string }[] = [];
                                  if (inDev !== null && inDev > 0)
                                    exceptions.push({ label: `Late +${inDev}m`, color: "bg-yellow-100 text-yellow-700" });
                                  if (outDev !== null && outDev < 0)
                                    exceptions.push({ label: `Early Out ${outDev}m`, color: "bg-orange-100 text-orange-700" });
                                  if (outDev !== null && outDev > 0)
                                    exceptions.push({ label: `OT +${outDev}m`, color: "bg-red-100 text-red-700" });

                                  const noteOpen = tsNotesExpanded.has(e.id);
                                  const toggleNote = () => setTsNotesExpanded((prev) => {
                                    const n = new Set(prev);
                                    noteOpen ? n.delete(e.id) : n.add(e.id);
                                    return n;
                                  });

                                  return (
                                    <>
                                    <tr key={e.id} className={`hover:bg-gray-50 ${e.isScheduledOnly ? "bg-gray-50/60 italic" : isInProgress ? "bg-teal-50/40" : isStaleOpen ? "bg-red-50/40" : ""}`}>
                                      <td className="px-5 py-2.5 font-medium text-gray-700 whitespace-nowrap">
                                        {new Date(e.workDate + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
                                      </td>
                                      <td className="px-4 py-2.5 text-gray-700">
                                        {fmt(effectiveIn, e.timezone)}
                                        {e.adjustedClockInAt && <span className="text-gray-400 ml-1">(adj)</span>}
                                      </td>
                                      <td className="px-4 py-2.5 text-gray-700">
                                        {isInProgress
                                          ? <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-teal-100 text-teal-700">Working</span>
                                          : isStaleOpen
                                          ? <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700" title="Never clocked out — needs review">Never Clocked Out</span>
                                          : <>
                                              {fmt(effectiveOut, e.timezone)}
                                              {e.adjustedClockOutAt && <span className="text-gray-400 ml-1">(adj)</span>}
                                            </>}
                                      </td>
                                      <td className="px-4 py-2.5 text-gray-500">
                                        {e.scheduledStart && e.scheduledEnd
                                          ? `${e.scheduledStart} – ${e.scheduledEnd}`
                                          : "—"}
                                      </td>
                                      <td className="px-4 py-2.5 text-gray-600">
                                        {fmtMins(e.totalBreakMinutes)}
                                        {e.breaks.length > 0 && (
                                          <div className="mt-0.5 space-y-0.5">
                                            {e.breaks.map((b) => (
                                              <div key={b.id} className="text-[11px] text-gray-400 whitespace-nowrap">
                                                {fmt(b.breakStartAt, e.timezone)} – {fmt(b.breakEndAt, e.timezone)}
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </td>
                                      <td className="px-4 py-2.5 font-semibold text-gray-800">{fmtMins(e.totalWorkedMinutes)}</td>
                                      <td className="px-4 py-2.5">
                                        {(e.totalOvertimeMinutes ?? 0) > 0
                                          ? <span className="text-red-600 font-semibold">+{fmtMins(e.totalOvertimeMinutes)}</span>
                                          : <span className="text-gray-300">—</span>}
                                      </td>
                                      <td className="px-4 py-2.5">
                                        {e.isScheduledOnly
                                          ? <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-600">Not Clocked In</span>
                                          : exceptions.length === 0
                                          ? <span className="text-green-600 font-medium">On Time</span>
                                          : <div className="flex flex-wrap gap-1">
                                              {exceptions.map((ex) => (
                                                <span key={ex.label} className={`px-1.5 py-0.5 rounded text-xs font-medium ${ex.color}`}>
                                                  {ex.label}
                                                </span>
                                              ))}
                                            </div>}
                                      </td>
                                      <td className="px-4 py-2.5">
                                        {e.isScheduledOnly ? null : (() => {
                                          const activeExcl = (e.exclusions ?? []).filter((x) => !x.restoredAt);
                                          const totalDeducted = activeExcl.reduce((s, x) => s + x.excludedMinutes, 0);
                                          return (
                                            <button
                                              onClick={() => { setExclEntry(e); setExclStart(""); setExclEnd(""); setExclReason(""); }}
                                              title="Manage exclusions"
                                              className={`inline-flex items-center gap-1 text-xs font-medium rounded px-2 py-0.5 transition ${activeExcl.length > 0 ? "bg-orange-100 text-orange-700 hover:bg-orange-200" : "text-gray-400 hover:text-gray-600"}`}
                                            >
                                              <Scissors className="h-3 w-3" />
                                              {activeExcl.length > 0 ? `-${totalDeducted}m` : "Add"}
                                            </button>
                                          );
                                        })()}
                                      </td>
                                      <td className="px-4 py-2.5 text-right whitespace-nowrap">
                                        {e.isScheduledOnly ? (
                                          <span className="text-gray-400 text-xs">Scheduled shift, no clock entry</span>
                                        ) : (
                                        <div className="inline-flex items-center gap-2">
                                          <button
                                            onClick={toggleNote}
                                            title={noteOpen ? "Hide note" : (e.notes ? "View note" : "Add note")}
                                            className={`inline-flex items-center gap-1 font-medium transition ${noteOpen || e.notes ? "text-yellow-600 hover:text-yellow-800" : "text-gray-400 hover:text-gray-600"}`}
                                          >
                                            <MessageSquare className="h-3.5 w-3.5" />
                                          </button>
                                          <button
                                            onClick={() => openEditTimes(e)}
                                            title="Edit times"
                                            className="inline-flex items-center gap-1 text-purple-600 hover:text-purple-800 font-medium transition"
                                          >
                                            <Edit2 className="h-3.5 w-3.5" />
                                          </button>
                                          <button
                                            onClick={() => {
                                              if (!window.confirm(`Delete this shift for ${e.memberName ?? "this team member"} on ${e.workDate}? This cannot be undone.`)) return;
                                              void deleteEntry(e.id, "timesheets");
                                            }}
                                            disabled={deletingTsId === e.id}
                                            title="Delete entry"
                                            className="inline-flex items-center gap-1 text-red-400 hover:text-red-600 font-medium transition disabled:opacity-40"
                                          >
                                            {deletingTsId === e.id
                                              ? <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                                              : <Trash2 className="h-3.5 w-3.5" />}
                                          </button>
                                        </div>
                                        )}
                                      </td>
                                    </tr>
                                    {noteOpen && (
                                      <tr key={`note-${e.id}`} className="bg-yellow-50 border-t border-yellow-100">
                                        <td colSpan={10} className="px-5 py-3">
                                          <div className="flex items-start gap-2 text-xs text-gray-700">
                                            <MessageSquare className="h-3.5 w-3.5 text-yellow-500 mt-0.5 flex-shrink-0" />
                                            <div className="flex-1">
                                              {e.notes
                                                ? <p className="text-gray-700 leading-relaxed">{e.notes}</p>
                                                : <p className="text-gray-400 italic">No note for this shift.</p>}
                                              <button
                                                onClick={() => { openOverride(e); toggleNote(); }}
                                                className="mt-1.5 text-blue-600 hover:text-blue-800 font-medium"
                                              >
                                                {e.notes ? "Edit note" : "Add note"}
                                              </button>
                                            </div>
                                          </div>
                                        </td>
                                      </tr>
                                    )}
                                    </>
                                  );
                                })}
                            </tbody>
                            {/* Period total row */}
                            <tfoot>
                              <tr className="bg-blue-50 border-t border-blue-100">
                                <td colSpan={5} className="px-5 py-2.5 text-xs font-semibold text-blue-700">Period Total</td>
                                <td className="px-4 py-2.5 text-sm font-bold text-blue-700">{fmtMins(totalWorked)}</td>
                                <td className="px-4 py-2.5 text-sm font-bold text-red-600">{totalOT > 0 ? `+${fmtMins(totalOT)}` : "—"}</td>
                                <td className="px-4 py-2.5 text-xs text-blue-500">{empEntries.length} shift{empEntries.length !== 1 ? "s" : ""}</td>
                                <td />
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      )}

      {/* ── CLOCK-IN ERRORS TAB ────────────────────────────────────────── */}
      {tab === "errors" && (() => {
        const filtered = errorEntries.filter((e) => {
          if (errorSearch && !(e.memberFirstName ?? e.memberName.split(" ")[0]).toLowerCase().startsWith(errorSearch.toLowerCase())) return false;
          if (errorRegion && e.regionId !== errorRegion) return false;
          if (errorRole && e.role !== errorRole) return false;
          return true;
        });
        const allSelected = filtered.length > 0 && filtered.every((e) => selectedErrors.has(e.id));

        const toggleAll = () => {
          if (allSelected) {
            setSelectedErrors((prev) => { const n = new Set(prev); filtered.forEach((e) => n.delete(e.id)); return n; });
          } else {
            setSelectedErrors((prev) => { const n = new Set(prev); filtered.forEach((e) => n.add(e.id)); return n; });
          }
        };

        const errorBadge = (type: string) => {
          if (type === "still_clocked_in") return <span className="px-1.5 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-700">Still In</span>;
          if (type === "long_shift")        return <span className="px-1.5 py-0.5 rounded text-xs font-semibold bg-orange-100 text-orange-700">Long Shift</span>;
          if (type === "duplicate")         return <span className="px-1.5 py-0.5 rounded text-xs font-semibold bg-yellow-100 text-yellow-700">Duplicate</span>;
          return null;
        };

        return (
          <div className="space-y-4">
            {/* Header controls */}
            <div className="bg-white rounded-lg shadow p-4 flex flex-wrap items-center gap-3">
              <input
                type="text"
                value={errorSearch}
                onChange={(e) => setErrorSearch(e.target.value)}
                placeholder="Search by first name..."
                className="rounded-lg border border-gray-200 text-sm px-3 py-1.5 w-48 focus:outline-none focus:ring-2 focus:ring-red-400"
              />
              <select value={errorRegion} onChange={(e) => setErrorRegion(e.target.value)}
                className="rounded-lg border border-gray-200 text-sm px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-red-400">
                <option value="">All Regions</option>
                {tsRegions.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
              <select value={errorRole} onChange={(e) => setErrorRole(e.target.value)}
                className="rounded-lg border border-gray-200 text-sm px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-red-400">
                <option value="">All Roles</option>
                <option value="pcc_incoming">PCC - Incoming</option>
                <option value="pcc_outbound">PCC - Outbound</option>
                <option value="team_lead">Team Lead</option>
                <option value="onboarding">Onboarding</option>
                <option value="operations_manager">Operations Manager</option>
                <option value="kpi">KPI</option>
                <option value="upper_management">Upper Management</option>
                <option value="qa">QA</option>
                <option value="outbound_mod">Outbound MOD</option>
                <option value="sales_marketing">Sales/Marketing</option>
                <option value="tech_dev_support">Tech/Dev Support</option>
                <option value="hr">HR</option>
                <option value="accounting">Accounting</option>
              </select>
              <button onClick={() => void loadErrors()}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 text-sm text-gray-600 hover:bg-gray-50 transition">
                <RefreshCw className={`h-4 w-4 ${errorsLoading ? "animate-spin" : ""}`} /> Refresh
              </button>
              <button
                onClick={exportErrors}
                disabled={filtered.length === 0}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 disabled:opacity-40 transition"
              >
                <Download className="h-4 w-4" /> Export CSV
              </button>
              {selectedErrors.size > 0 && (
                <button
                  onClick={() => void deleteSelected()}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 transition"
                >
                  <X className="h-4 w-4" /> Delete Selected ({selectedErrors.size})
                </button>
              )}
              <span className="ml-auto text-xs text-gray-400">{filtered.length} issue{filtered.length !== 1 ? "s" : ""} detected</span>
            </div>

            {errorsLoading ? (
              <div className="bg-white rounded-lg shadow p-10 text-center text-sm text-gray-400">Scanning for errors...</div>
            ) : filtered.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-10 text-center">
                <CheckCircle className="h-10 w-10 text-green-400 mx-auto mb-3" />
                <p className="text-sm font-medium text-gray-700">No clock-in errors detected</p>
                <p className="text-xs text-gray-400 mt-1">All shifts look clean — no long shifts or duplicate entries.</p>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200 text-left text-xs text-gray-500 uppercase tracking-wide">
                      <th className="px-4 py-3 w-10">
                        <input type="checkbox" checked={allSelected} onChange={toggleAll}
                          className="rounded border-gray-300 text-red-600 focus:ring-red-400" />
                      </th>
                      <th className="px-4 py-3 font-semibold text-blue-600">Name</th>
                      <th className="px-4 py-3 font-semibold text-blue-600">Date</th>
                      <th className="px-4 py-3 font-semibold text-blue-600">Start Time</th>
                      <th className="px-4 py-3 font-semibold text-blue-600">End Time (Date)</th>
                      <th className="px-4 py-3 font-semibold text-blue-600">Duration (Hours)</th>
                      <th className="px-4 py-3 font-semibold text-blue-600">Issues</th>
                      <th className="px-4 py-3 font-semibold text-blue-600 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filtered.map((e) => {
                      const inTime  = new Date(e.clockInAt).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", timeZone: DISPLAY_TZ });
                      const outDate = e.clockOutAt ? new Date(e.clockOutAt) : null;
                      const outStr  = outDate
                        ? outDate.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", timeZone: DISPLAY_TZ })
                          + " (" + outDate.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric", timeZone: DISPLAY_TZ }) + ")"
                        : "—";
                      const isLong = e.durationMins >= 24 * 60;

                      return (
                        <tr key={e.id} className={`hover:bg-red-50 ${selectedErrors.has(e.id) ? "bg-red-50" : ""}`}>
                          <td className="px-4 py-3">
                            <input type="checkbox" checked={selectedErrors.has(e.id)}
                              onChange={() => setSelectedErrors((prev) => { const n = new Set(prev); prev.has(e.id) ? n.delete(e.id) : n.add(e.id); return n; })}
                              className="rounded border-gray-300 text-red-600 focus:ring-red-400" />
                          </td>
                          <td className="px-4 py-3 font-medium text-gray-900">{e.memberName}</td>
                          <td className="px-4 py-3 text-gray-600">{new Date(e.workDate + "T00:00:00").toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" })}</td>
                          <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                            <span className="inline-flex items-center gap-1">
                              <span className="text-blue-400">▣</span> {inTime}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                            {e.clockOutAt
                              ? <span className="inline-flex items-center gap-1"><span className="text-blue-400">▣</span> {outStr}</span>
                              : <span className="text-red-500 font-medium">Still clocked in</span>}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`font-mono font-semibold text-sm ${isLong ? "text-red-600" : "text-orange-600"}`}>
                              {e.durationLabel}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                              {e.errors.map((t) => <span key={t}>{errorBadge(t)}</span>)}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            <button
                              onClick={() => {
                                const clockEntry = entries.find((en) => en.id === e.id) ?? {
                                  id: e.id, clockInAt: e.clockInAt, clockOutAt: e.clockOutAt,
                                  adjustedClockInAt: null, adjustedClockOutAt: null,
                                  workDate: e.workDate, memberName: e.memberName,
                                  scheduledStart: null, scheduledEnd: null, timezone: e.timezone,
                                } as unknown as ClockEntry;
                                setEditEntry(clockEntry);
                                setEditClockIn(toDatetimeLocalInTz(e.clockInAt, e.timezone));
                                setEditClockOut(toDatetimeLocalInTz(e.clockOutAt, e.timezone));
                                setEditReason("");
                              }}
                              className="text-green-600 hover:text-green-800 font-medium text-sm mr-3"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => void ignoreError(e.id)}
                              disabled={ignoringId === e.id}
                              className="text-yellow-600 hover:text-yellow-800 font-medium text-sm mr-3 disabled:opacity-40"
                              title="Hide from errors list without deleting the time record"
                            >
                              {ignoringId === e.id ? "..." : "Ignore"}
                            </button>
                            <button
                              onClick={() => void deleteEntry(e.id)}
                              disabled={deletingId === e.id}
                              className="text-red-500 hover:text-red-700 font-medium text-sm disabled:opacity-40"
                            >
                              {deletingId === e.id ? "..." : "Delete"}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Add Time Record modal (manager) ─────────────────────────────── */}
      {showAddRecord && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-green-600">
                <PlusCircle className="h-5 w-5" />
                <h2 className="text-base font-semibold">Add Time Record</h2>
              </div>
              <button onClick={() => setShowAddRecord(false)}><X className="h-4 w-4 text-gray-400 hover:text-gray-600" /></button>
            </div>
            <p className="text-xs text-gray-400">Creates an attendance record directly — no approval needed.</p>

            <div className="relative">
              <label className="block text-xs font-medium text-gray-700 mb-1">Employee <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={arSearch}
                onChange={(e) => { setArSearch(e.target.value); setArMemberId(""); setArDropdownOpen(true); }}
                onFocus={() => setArDropdownOpen(true)}
                onBlur={() => setTimeout(() => setArDropdownOpen(false), 150)}
                placeholder="Search employee..."
                className="w-full rounded-lg border border-gray-300 text-sm p-2 focus:outline-none focus:ring-2 focus:ring-green-400"
              />
              {arDropdownOpen && (
                <ul className="absolute z-50 mt-1 w-full max-h-48 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg text-sm">
                  {arMembers
                    .filter((m) => m.name.toLowerCase().includes(arSearch.toLowerCase()))
                    .map((m) => (
                      <li
                        key={m.id}
                        onMouseDown={() => { setArMemberId(m.id); setArSearch(m.name); setArDropdownOpen(false); }}
                        className={`px-3 py-2 cursor-pointer hover:bg-green-50 ${arMemberId === m.id ? "bg-green-50 font-medium" : ""}`}
                      >
                        {m.name}
                      </li>
                    ))}
                  {arMembers.filter((m) => m.name.toLowerCase().includes(arSearch.toLowerCase())).length === 0 && (
                    <li className="px-3 py-2 text-gray-400">No employees found</li>
                  )}
                </ul>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Date <span className="text-red-500">*</span></label>
              <input type="date" value={arDate} onChange={(e) => setArDate(e.target.value)}
                className="w-full rounded-lg border border-gray-300 text-sm p-2 focus:outline-none focus:ring-2 focus:ring-green-400" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Clock In <span className="text-red-500">*</span>
                  {arMemberId && <span className="ml-1 text-xs text-gray-400 font-normal">({tzLabel(arMembers.find(m => m.id === arMemberId)?.timezone)})</span>}
                </label>
                <input type="datetime-local" value={arClockIn} onChange={(e) => setArClockIn(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 text-sm p-2 focus:outline-none focus:ring-2 focus:ring-green-400" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Clock Out <span className="text-red-500">*</span>
                  {arMemberId && <span className="ml-1 text-xs text-gray-400 font-normal">({tzLabel(arMembers.find(m => m.id === arMemberId)?.timezone)})</span>}
                </label>
                <input type="datetime-local" value={arClockOut} onChange={(e) => setArClockOut(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 text-sm p-2 focus:outline-none focus:ring-2 focus:ring-green-400" />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Break Duration</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  max="720"
                  value={arBreakMins}
                  onChange={(e) => setArBreakMins(e.target.value === "" ? "" : Number(e.target.value))}
                  placeholder="0"
                  className="w-24 rounded-lg border border-gray-300 text-sm p-2 focus:outline-none focus:ring-2 focus:ring-green-400"
                />
                <span className="text-xs text-gray-500">minutes</span>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
              <input type="text" value={arNotes} onChange={(e) => setArNotes(e.target.value)}
                placeholder="Optional note..."
                className="w-full rounded-lg border border-gray-300 text-sm p-2 focus:outline-none focus:ring-2 focus:ring-green-400" />
            </div>

            <div className="flex gap-2 pt-1">
              <button onClick={() => setShowAddRecord(false)} className="flex-1 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={() => void submitAddRecord()} disabled={savingAr}
                className="flex-1 py-2 rounded-lg bg-green-600 text-white text-sm font-semibold hover:bg-green-700 disabled:opacity-50">
                {savingAr ? "Creating..." : "Create Record"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Times modal ────────────────────────────────────────────── */}
      {editEntry && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <Edit2 className="h-4 w-4 text-purple-500" /> Edit Clock Times
              </h2>
              <button onClick={closeEditTimes} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>

            {/* Entry info */}
            <div className="bg-gray-50 rounded-lg p-3 text-sm space-y-1 text-gray-600">
              <div className="flex justify-between">
                <span>Employee</span>
                <span className="font-medium text-gray-800">{editEntry.memberName ?? "—"}</span>
              </div>
              <div className="flex justify-between">
                <span>Date</span>
                <span className="font-medium text-gray-800">{editEntry.workDate}</span>
              </div>
              <div className="flex justify-between">
                <span>Original In / Out</span>
                <span className="font-medium text-gray-800">
                  {fmt(editEntry.clockInAt, editEntry.timezone)} – {fmt(editEntry.clockOutAt, editEntry.timezone)}
                </span>
              </div>
              {(editEntry.adjustedClockInAt || editEntry.adjustedClockOutAt) && (
                <div className="flex justify-between text-purple-600">
                  <span>Previously Adjusted</span>
                  <span className="font-medium">
                    {fmt(editEntry.adjustedClockInAt, editEntry.timezone)} – {fmt(editEntry.adjustedClockOutAt, editEntry.timezone)}
                  </span>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Clock-In Time <span className="text-red-500">*</span>
                <span className="ml-1 text-xs text-gray-400 font-normal">({tzLabel(editEntry?.timezone)})</span>
              </label>
              <input
                type="datetime-local"
                value={editClockIn}
                onChange={(e) => setEditClockIn(e.target.value)}
                className="w-full rounded-lg border border-gray-300 text-sm p-2 focus:outline-none focus:ring-2 focus:ring-purple-400"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Clock-Out Time <span className="text-gray-400 font-normal">(leave blank if still clocked in)</span>
                <span className="ml-1 text-xs text-gray-400 font-normal">({tzLabel(editEntry?.timezone)})</span>
              </label>
              <input
                type="datetime-local"
                value={editClockOut}
                onChange={(e) => setEditClockOut(e.target.value)}
                className="w-full rounded-lg border border-gray-300 text-sm p-2 focus:outline-none focus:ring-2 focus:ring-purple-400"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Break Duration <span className="text-gray-400 font-normal">(leave blank to keep existing breaks)</span>
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  max="720"
                  value={editBreakMins}
                  onChange={(e) => setEditBreakMins(e.target.value === "" ? "" : Number(e.target.value))}
                  placeholder={editEntry ? String(Math.round(editEntry.totalBreakMinutes)) : "0"}
                  className="w-24 rounded-lg border border-gray-300 text-sm p-2 focus:outline-none focus:ring-2 focus:ring-purple-400"
                />
                <span className="text-sm text-gray-500">minutes</span>
                {editEntry && editEntry.totalBreakMinutes > 0 && (
                  <span className="text-xs text-gray-400">
                    (currently {Math.round(editEntry.totalBreakMinutes)}m)
                  </span>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason <span className="text-red-500">*</span></label>
              <textarea
                rows={2}
                value={editReason}
                onChange={(e) => setEditReason(e.target.value)}
                className="w-full rounded-lg border border-gray-300 text-sm p-2 focus:outline-none focus:ring-2 focus:ring-purple-400"
                placeholder="e.g. System error, employee reported wrong time..."
              />
            </div>

            <p className="text-xs text-gray-400">Original times are preserved for audit. Edited times will be shown as the effective time.</p>

            <div className="flex gap-2 pt-1">
              <button onClick={closeEditTimes} className="flex-1 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={submitEditTimes} disabled={savingEdit}
                className="flex-1 py-2 rounded-lg bg-purple-600 text-white text-sm font-semibold hover:bg-purple-700 disabled:opacity-50">
                {savingEdit ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add Note modal ──────────────────────────────────────────────── */}
      {activeEntry && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <Edit2 className="h-4 w-4 text-blue-500" /> Add Note
              </h2>
              <button onClick={closeOverride} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>

            <div className="bg-gray-50 rounded-lg p-3 text-sm space-y-1 text-gray-600">
              <div className="flex justify-between">
                <span>Employee</span>
                <span className="font-medium text-gray-800">{activeEntry.memberName ?? "—"}</span>
              </div>
              <div className="flex justify-between">
                <span>Date</span>
                <span className="font-medium text-gray-800">{activeEntry.workDate}</span>
              </div>
              <div className="flex justify-between">
                <span>In / Out</span>
                <span className="font-medium text-gray-800">
                  {fmt(activeEntry.clockInAt, activeEntry.timezone)} – {fmt(activeEntry.clockOutAt, activeEntry.timezone)}
                </span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Note <span className="text-red-500">*</span></label>
              <textarea
                rows={3}
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                className="w-full rounded-lg border border-gray-300 text-sm p-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                placeholder="e.g. Employee reported system issue, approved exception..."
              />
            </div>

            <div className="flex gap-2 pt-1">
              <button onClick={closeOverride} className="flex-1 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={submitOverride} disabled={saving}
                className="flex-1 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">
                {saving ? "Saving..." : "Save Note"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Exclusion Modal ──────────────────────────────────────────────── */}
      {exclEntry && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 space-y-5">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <Scissors className="h-5 w-5 text-orange-500" />
                Time Exclusions
              </h2>
              <button onClick={() => setExclEntry(null)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <p className="text-sm text-gray-500">
              Shift: <span className="font-medium text-gray-700">{exclEntry.memberName}</span> —{" "}
              {new Date(exclEntry.workDate + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
            </p>

            {/* Existing exclusions */}
            {(exclEntry.exclusions ?? []).length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Existing Exclusions</p>
                {(exclEntry.exclusions ?? []).map((x) => (
                  <div key={x.id} className={`flex items-center justify-between rounded-lg px-3 py-2 text-sm ${x.restoredAt ? "bg-gray-50 text-gray-400" : "bg-orange-50 text-orange-800"}`}>
                    <div>
                      <span className="font-medium">{x.startTime} – {x.endTime}</span>
                      <span className="mx-2 text-xs">({x.excludedMinutes}m deducted)</span>
                      <span className="text-xs">{x.reason}</span>
                      {x.restoredAt && <span className="ml-2 text-xs text-green-600 font-medium">Restored</span>}
                    </div>
                    {!x.restoredAt && (
                      <button
                        onClick={() => void restoreExclusion(x.id)}
                        disabled={restoringExclId === x.id}
                        title="Restore — add hours back"
                        className="ml-3 inline-flex items-center gap-1 text-xs text-green-600 hover:text-green-800 font-medium disabled:opacity-40"
                      >
                        {restoringExclId === x.id
                          ? <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                          : <RotateCcw className="h-3.5 w-3.5" />}
                        Restore
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Add new exclusion */}
            <div className="space-y-3 border-t border-gray-100 pt-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Add New Exclusion</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Start Time</label>
                  <input
                    type="time"
                    value={exclStart}
                    onChange={(e) => setExclStart(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 text-sm p-2 focus:outline-none focus:ring-2 focus:ring-orange-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">End Time</label>
                  <input
                    type="time"
                    value={exclEnd}
                    onChange={(e) => setExclEnd(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 text-sm p-2 focus:outline-none focus:ring-2 focus:ring-orange-400"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Reason</label>
                <input
                  type="text"
                  value={exclReason}
                  onChange={(e) => setExclReason(e.target.value)}
                  placeholder="e.g. System outage 9–10 AM"
                  className="w-full rounded-lg border border-gray-300 text-sm p-2 focus:outline-none focus:ring-2 focus:ring-orange-400"
                />
              </div>
              {exclStart && exclEnd && exclStart < exclEnd && (
                <p className="text-xs text-orange-600 font-medium">
                  Will deduct{" "}
                  {((): number => {
                    const [sh, sm] = exclStart.split(":").map(Number);
                    const [eh, em] = exclEnd.split(":").map(Number);
                    return (eh * 60 + em) - (sh * 60 + sm);
                  })()}
                  {" "}minutes from payable hours.
                </p>
              )}
            </div>

            <div className="flex gap-2 pt-1">
              <button onClick={() => setExclEntry(null)} className="flex-1 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50">
                Close
              </button>
              <button
                onClick={() => void addExclusion()}
                disabled={savingExcl || !exclStart || !exclEnd || !exclReason.trim() || exclStart >= exclEnd}
                className="flex-1 py-2 rounded-lg bg-orange-500 text-white text-sm font-semibold hover:bg-orange-600 disabled:opacity-50"
              >
                {savingExcl ? "Saving..." : "Add Exclusion"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Summary Card ─────────────────────────────────────────────────────────────

function SummaryCard({
  icon, bg, label, value, sub, subColor,
}: {
  icon: ReactNode;
  bg: string;
  label: string;
  value: string | number;
  sub?: string;
  subColor?: string;
}) {
  return (
    <div className="bg-white rounded-lg shadow p-4 flex items-center gap-3">
      <div className={`h-10 w-10 rounded-full flex items-center justify-center ${bg}`}>{icon}</div>
      <div>
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-2xl font-bold text-gray-800">{value}</p>
        {sub && <p className={`text-xs font-medium ${subColor}`}>{sub}</p>}
      </div>
    </div>
  );
}
